import apiClient from './apiService';

const BASE = '/api/v1/adt';

// Allowed values must match the DB CHECK on ADT_procedure_templates.procedure_type.
export type ProcedureType =
  | 'Checklist'
  | 'Questionnaire'
  | 'Form'
  | 'Rating'
  | 'Confirmation'
  | 'PhysicalCheck'
  | 'Walkthrough'              // legacy / orphan only — not creatable
  | 'WalkthroughDesign'        // mirrored from SOP "Enable Test of Design"
  | 'WalkthroughCompliance'    // mirrored from SOP "Enable Test of Compliance"
  | 'RiskAssessment'
  | 'ControlTest';

// BRD Node B1.N1: Agency is a Group-level entity — no entity_id on the record.
export interface Agency {
  id: number;
  group_id?: number | null;
  /** @deprecated entity_id is null for agencies created after migration 055 */
  entity_id?: number | null;
  agency_code?: string | null;
  audit_agency_name: string;
  description?: string | null;
  contact_head_name?: string | null;
  phone?: string | null;
  email?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  /** 1 = visible to all entities in the group; 0 = own-entity only */
  is_global?: number;
  /** 1 = Active, 0 = Inactive (inactivated), -1 = Deleted (not exposed via UI) */
  is_active?: number;
  is_approved?: number;
  /** Internal/External character (mig 132). Drives audit-role auto-assignment on
   *  member link: External → External Auditor (exclusive); Internal → Internal
   *  Audit Team. Set at creation; immutable thereafter. */
  agency_kind?: 'Internal' | 'External';
  /** Audit-plan timeline (APM-5.1): MONTHLY | QUARTERLY | HALF_YEARLY | ANNUAL */
  plan_periodicity?: 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'ANNUAL';
  /** APM-5.2 — the year the agency reports in, and its audit period (periods under audit). */
  plan_year_type?: 'CALENDAR' | 'FINANCIAL';
  plan_start_period_id?: number | null;
  plan_end_period_id?: number | null;
  plan_start_period_label?: string | null;
  plan_end_period_label?: string | null;
  /** USR_users.id of the Agency Head / Point of Contact */
  head_user_id?: number | null;
  created_by?: number | null;
  created_on?: string;
  modified_by?: number | null;
  modified_on?: string;
  // System code (AGY-N) — null until approved.
  code?: string | null;
  code_seq?: number | null;
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
}

/** BRD#746: name (required) + description; plus Head (Point of Contact).
 * Pick ONE of:
 *   (a) link existing user by setting head_user_id, OR
 *   (b) create new user by setting head_name + head_email + head_password.
 */
export interface AgencyCreatePayload {
  audit_agency_name: string;
  description?: string | null;
  /** Internal/External character — set once at creation (defaults to External). */
  agency_kind?: 'Internal' | 'External';
  /** Audit-plan timeline (APM-5.1): MONTHLY | QUARTERLY | HALF_YEARLY | ANNUAL */
  plan_periodicity?: 'MONTHLY' | 'QUARTERLY' | 'HALF_YEARLY' | 'ANNUAL';
  /** APM-5.2 — the year the agency reports in, and its audit period (periods under audit). */
  plan_year_type?: 'CALENDAR' | 'FINANCIAL';
  plan_start_period_id?: number | null;
  plan_end_period_id?: number | null;
  plan_start_period_label?: string | null;
  plan_end_period_label?: string | null;
  /** 1 = visible to all entities in the group; 0 = own-entity only */
  is_global?: number;
  /** (a) link existing USR_users.id as Agency Head */
  head_user_id?: number | null;
  clear_plan_end_period?: boolean;
  /** (b) create new user as Agency Head */
  head_name?: string;
  head_email?: string;
  head_password?: string;
}

export type AgencyUpdatePayload = Partial<Omit<Agency, 'id' | 'created_on' | 'group_id' | 'entity_id'>> & {
  /** Make the agency's audit period open-ended again (APM-5.2). */
  clear_plan_end_period?: boolean;
};

export interface AgencyMember {
  id: number;
  entity_id?: number | null;
  agency_id: number;
  user_id: number;
  /** 'Staff' | 'Agency Head' — BRD Node B1.N2 */
  member_role?: string | null;
  is_active?: number;
  is_approved?: number;
  created_by?: number | null;
  created_on?: string;
  // Populated by GET /agencies/{id}/members (JOIN USR_users) so the UI shows names.
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  login_id?: string | null;
}
export type AgencyMemberCreatePayload = Omit<AgencyMember, 'id' | 'created_on' | 'first_name' | 'last_name' | 'email' | 'login_id'>;

/** A user eligible to be linked to an agency (GET /agencies/{id}/link-candidates). */
export interface AgencyLinkCandidate {
  user_id: number;
  name: string;
  email?: string | null;
}

export interface AgencyHead {
  id: number;
  agency_id: number;
  user_id: number;
  member_role: string;
  first_name: string;
  last_name: string;
  email?: string | null;
  login_id?: string | null;
  is_active: number;
  created_on?: string;
}

// BRD#857: allowed evidence kinds
export type EvidenceKind = 'file' | 'screenshot' | 'text' | 'mixed';
export const EVIDENCE_KIND_OPTIONS: EvidenceKind[] = ['file', 'screenshot', 'text', 'mixed'];

export interface ProcedureTemplate {
  id: number;
  entity_id?: number | null;
  group_id?: number | null;
  procedure_name: string;
  procedure_description?: string | null;
  /** BRD#852: GRC-Common | Agency-Private */
  ownership_scope?: OwnershipScope | null;
  /** null when scope = GRC-Common */
  owner_agency_id?: number | null;
  procedure_type: ProcedureType;
  effective_start_date?: string | null;
  /** BRD#857: evidence declarations */
  evidence_mandatory?: boolean | null;
  /** comma-separated subset of: file, screenshot, text, mixed */
  evidence_kinds?: string | null;
  linked_sop_id?: number | null;
  linked_risk_assessment_template_id?: number | null;
  linked_control_test_template_id?: number | null;
  /** BRD#860: bumped on every PUT */
  current_version?: number | null;
  is_active?: number;
  is_approved?: number;
  created_by?: number | null;
  created_on?: string;
  modified_by?: number | null;
  modified_on?: string;
  // Unified system code (PRC-N) for the generic Procedures tab — null until approved.
  code?: string | null;
  code_seq?: number | null;
  // Maker-checker workflow state — drives the status dot on the Procedures tab.
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
}

export type ProcedureTemplateCreatePayload = Omit<ProcedureTemplate, 'id' | 'created_on' | 'modified_on' | 'modified_by' | 'current_version' | 'group_id' | 'code' | 'code_seq'>;
export type ProcedureTemplateUpdatePayload = Partial<ProcedureTemplateCreatePayload>;

// BRD#911
export type CadenceValue = 'Annual' | 'Quarterly' | 'Monthly' | 'Ad-hoc';
export const CADENCE_OPTIONS: { value: CadenceValue; label: string }[] = [
  { value: 'Annual',    label: 'Annual' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Monthly',   label: 'Monthly' },
  { value: 'Ad-hoc',   label: 'Ad-hoc' },
];

// BRD#924
export type OwnershipScope = 'GRC-Common' | 'Agency-Private';
export const OWNERSHIP_SCOPE_OPTIONS: { value: OwnershipScope; label: string }[] = [
  { value: 'GRC-Common',     label: 'GRC-Common' },
  { value: 'Agency-Private', label: 'Agency-Private' },
];

// BRD#925: process-audit types forbidden in Agency-Private templates
export const PROCESS_AUDIT_TYPES: ProcedureType[] = [
  'Walkthrough', 'WalkthroughDesign', 'WalkthroughCompliance',
  'RiskAssessment', 'ControlTest',
];

export interface ProgramTemplate {
  id: number;
  entity_id?: number | null;
  audit_program_code: string;
  audit_program_name: string;
  audit_program_description?: string | null;
  agency_id?: number | null;
  effective_start_date?: string | null;
  /** BRD#911: mandatory cadence */
  cadence?: CadenceValue | null;
  /** BRD#924: immutable after creation */
  ownership_scope?: OwnershipScope | null;
  current_version?: number | null;
  is_active?: number;
  is_approved?: number;
  created_by?: number | null;
  created_on?: string;
  // System-generated code (APT-N) — null until approved.
  code?: string | null;
  // Maker-checker workflow state — drives the status dot.
  workflow_state?: 'draft' | 'submitted' | 'approved' | 'rejected';
}

export type ProgramTemplateCreatePayload = Omit<ProgramTemplate, 'id' | 'created_on' | 'current_version'>;
// ownership_scope omitted from update payload (immutable per BRD#924)
export type ProgramTemplateUpdatePayload = Partial<Omit<ProgramTemplateCreatePayload, 'ownership_scope'>>;

// --- Sub-template payloads (per procedure type) ---

export interface ChecklistTemplateItem {
  id: number;
  procedure_template_id: number;
  check_question: string;
  evidence_required?: boolean;
  evidence_type?: string | null;
  na_allowed?: boolean;
  is_active?: number;
}
export type ChecklistTemplateCreate = Omit<ChecklistTemplateItem, 'id'> & { entity_id?: number | null; created_by?: number | null };

export interface QuestionnaireTemplateItem {
  id: number;
  procedure_template_id: number;
  question_text: string;
  evidence_required?: boolean;
  evidence_type?: string | null;
  is_active?: number;
}

// --- Picker types for Walkthrough / RiskAssessment / ControlTest configurators ---
export interface SOPProcess {
  id: number;
  process_name: string;
  description?: string | null;
  process_status?: string;
  is_active?: number;
}

export interface RiskAssessmentTemplate {
  id: number;
  risk_assessment_name: string;
  frequency?: string | null;
  is_active?: number;
}

export interface ControlTestTemplate {
  id: number;
  testing_code: string;
  testing_description?: string | null;
  cadence?: string | null;
  is_active?: number;
}

export interface FormTemplateItem {
  id: number;
  procedure_template_id: number;
  form_question: string;
  answer_data_type: string;
  is_required?: boolean;
  options?: string | null;     // CSV of option labels for Dropdown
  is_active?: number;
}

export interface RatingTemplateItem {
  id: number;
  procedure_template_id: number;
  rating_question: string;
  maximum_score?: number;
  is_normalized?: boolean;
  is_active?: number;
}

export interface RatingOptionItem {
  id: number;
  rating_template_id: number;
  rating_option_text: string;
  rating_option_score: number;
  is_active?: number;
}

export interface ConfirmationTemplate {
  id: number;
  procedure_template_id: number;
  location_id?: number | null;
  // CSV of selected balance types — nullable since a row can be created with
  // only locations or notes filled in.
  balance_type?: string | null;
  geography_scope?: string | null;     // CSV of location IDs
  coverage_basis?: string | null;
  coverage_threshold?: string | null;
  notes?: string | null;
  is_active?: number;
}

export interface PhysicalCheckTemplate {
  id: number;
  procedure_template_id: number;
  location_id?: number | null;
  asset_type?: string | null;          // CSV of selected asset types
  geography_scope?: string | null;     // CSV of location IDs
  coverage_basis?: string | null;
  coverage_threshold?: string | null;
  notes?: string | null;
  is_active?: number;
}

export interface ProgramProcedureMap {
  id: number;
  program_template_id: number;
  procedure_template_id: number;
  /** BRD#918: ordering within the program template */
  sequence_no?: number | null;
  is_active?: number;
}

// Helper: pull `data` array out of paginated responses; pass through raw arrays.
function unwrap<T>(payload: any): T[] {
  if (Array.isArray(payload)) return payload as T[];
  if (Array.isArray(payload?.data)) return payload.data as T[];
  return [];
}

// BRD Node B2.N1 — Audit Staff
export interface StaffMember {
  member_id: number;
  agency_id: number;
  user_id: number;
  member_role: string;
  is_active: number;
  is_approved: number;
  created_on?: string;
  first_name: string;
  last_name: string;
  login_id?: string | null;
  email?: string | null;
  phone?: string | null;
  audit_agency_name: string;
}

export interface StaffCreatePayload {
  first_name: string;
  last_name: string;
  login_id: string;
  email: string;
  phone?: string;
}

// BRD Node B2.N4 — Audit Staff Role Assignment (Maker / Checker / Program Head)
export type StaffRoleName = 'Maker' | 'Checker' | 'Program Head';
export const STAFF_ROLE_OPTIONS: StaffRoleName[] = ['Maker', 'Checker', 'Program Head'];

export interface StaffRoleAssignment {
  id: number;
  member_id: number;
  agency_id: number;
  role_name: StaffRoleName;
  assigned_by: number;
  assigned_by_name?: string | null;
  is_active: number;
  created_on?: string | null;
  revoked_by?: number | null;
  revoked_on?: string | null;
}

export const staffService = {
  async list(agencyId?: number, isActive?: number): Promise<StaffMember[]> {
    const params: Record<string, any> = { limit: 100 };
    if (agencyId) params.agency_id = agencyId;
    if (isActive !== undefined) params.is_active = isActive;
    const resp = await apiClient.get(`${BASE}/staff`, { params });
    return unwrap<StaffMember>(resp.data);
  },
  async create(payload: StaffCreatePayload, agencyId?: number): Promise<any> {
    const params: Record<string, any> = {};
    if (agencyId) params.agency_id = agencyId;
    const resp = await apiClient.post(`${BASE}/staff`, payload, { params });
    return resp.data;
  },
  async update(memberId: number, payload: Omit<StaffCreatePayload, 'login_id'>): Promise<any> {
    const resp = await apiClient.put(`${BASE}/staff/${memberId}`, payload);
    return resp.data;
  },
  async inactivate(memberId: number): Promise<any> {
    const resp = await apiClient.patch(`${BASE}/staff/${memberId}/inactivate`);
    return resp.data;
  },
  async activate(memberId: number): Promise<any> {
    const resp = await apiClient.patch(`${BASE}/staff/${memberId}/activate`);
    return resp.data;
  },
  async getRoles(memberId: number): Promise<StaffRoleAssignment[]> {
    const resp = await apiClient.get(`${BASE}/staff/${memberId}/roles`);
    return Array.isArray(resp.data) ? resp.data : [];
  },
  async assignRole(memberId: number, roleName: StaffRoleName): Promise<StaffRoleAssignment> {
    const resp = await apiClient.post(`${BASE}/staff/${memberId}/roles`, { role_name: roleName });
    return resp.data;
  },
  async revokeRole(memberId: number, roleName: StaffRoleName): Promise<any> {
    const resp = await apiClient.delete(`${BASE}/staff/${memberId}/roles/${encodeURIComponent(roleName)}`);
    return resp.data;
  },
};

// --- Agencies (BRD Node B1.N1) ---
export const agencyService = {
  async list(): Promise<Agency[]> {
    const resp = await apiClient.get(`${BASE}/agencies`, { params: { limit: 100 } });
    return unwrap<Agency>(resp.data);
  },
  async create(payload: AgencyCreatePayload): Promise<Agency> {
    const resp = await apiClient.post(`${BASE}/agencies`, payload);
    return resp.data;
  },
  async update(id: number, payload: AgencyUpdatePayload): Promise<Agency> {
    const resp = await apiClient.put(`${BASE}/agencies/${id}`, payload);
    return resp.data;
  },
  /** BRD#754: only GRC Team may inactivate. Record is never deleted (BRD#755). */
  async inactivate(id: number): Promise<Agency> {
    const resp = await apiClient.patch(`${BASE}/agencies/${id}/inactivate`);
    return resp.data;
  },
  /** BRD#756: GRC Team may reactivate an Inactive Agency. */
  async reactivate(id: number): Promise<Agency> {
    const resp = await apiClient.patch(`${BASE}/agencies/${id}/reactivate`);
    return resp.data;
  },
};

// --- Agency members (which users belong to which agency) ---
export const agencyMemberService = {
  async list(): Promise<AgencyMember[]> {
    const resp = await apiClient.get(`${BASE}/agency_members`, { params: { limit: 100 } });
    return unwrap<AgencyMember>(resp.data);
  },
  async listForAgency(agencyId: number): Promise<AgencyMember[]> {
    // Dedicated endpoint returns members JOINed with USR_users so names show
    // (first_name/last_name/login_id) instead of "User #<id>".
    const resp = await apiClient.get(`${BASE}/agencies/${agencyId}/members`);
    // Only active members belong in the agency team view; a revoked head-row
    // (is_active=0) must not leak into the list or the head banner.
    return (resp.data as AgencyMember[]).filter(m => (m.is_active ?? 1) === 1);
  },
  /** Kind-aware candidate users for the "link user" picker: External agency →
   *  existing External Auditors only; Internal agency → internal staff (excludes
   *  External Auditors). Both exclude users already linked to this agency. */
  async linkCandidates(agencyId: number): Promise<AgencyLinkCandidate[]> {
    const resp = await apiClient.get(`${BASE}/agencies/${agencyId}/link-candidates`);
    return resp.data as AgencyLinkCandidate[];
  },
  /** Kind-aware head candidates for a NOT-YET-CREATED agency (no agency_id yet).
   *  External → External Auditors only; Internal → internal staff. */
  async headCandidatesByKind(agencyKind: 'Internal' | 'External'): Promise<AgencyLinkCandidate[]> {
    const resp = await apiClient.get(`${BASE}/agency-head-candidates`, { params: { agency_kind: agencyKind } });
    return resp.data as AgencyLinkCandidate[];
  },
  async create(payload: AgencyMemberCreatePayload): Promise<AgencyMember> {
    const resp = await apiClient.post(`${BASE}/agency_members`, payload);
    return resp.data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/agency_members/${id}`);
  },
  /** BRD#750 — designate a user as the Agency Head (replaces any existing head). */
  async designateHead(agencyId: number, userId: number): Promise<AgencyMember> {
    const resp = await apiClient.post(`${BASE}/agencies/${agencyId}/designate-head`, { user_id: userId });
    return resp.data;
  },
  /** Returns the current Agency Head record with user details, or null. */
  async getHead(agencyId: number): Promise<AgencyHead | null> {
    try {
      const resp = await apiClient.get(`${BASE}/agencies/${agencyId}/head`);
      return resp.data ?? null;
    } catch {
      return null;
    }
  },
};

export interface PagedResult<T> {
  items: T[];
  total: number;
}

export interface ProcedureTemplateListParams {
  page: number;
  limit: number;
  search?: string;
  type?: string;
  scope?: string;
  /** ACTIVE | INACTIVE | ALL — filtered server-side so meta.total matches. */
  status?: 'ACTIVE' | 'INACTIVE' | 'ALL';
}

// --- Procedure templates (the master) ---
/** Caller's audit-authoring persona — drives the portal Create flow. */
export interface MyAuditPersona {
  /** True when the caller is an external-party auditor (member of an External agency). */
  is_external: boolean;
  /** True only when the caller is the Agency Head (the audit lead) of an external agency. */
  can_author: boolean;
  /** First authored agency (back-compat); null for GRC/internal users. */
  agency_id: number | null;
  agency_name: string | null;
  /** All agencies the lead heads — the only agencies offered as owning agency. */
  authored_agencies: { id: number; name: string }[];
}

export const auditPersonaService = {
  async me(): Promise<MyAuditPersona> {
    const resp = await apiClient.get(`${BASE}/my-audit-persona`);
    return resp.data as MyAuditPersona;
  },
};

export const procedureTemplateService = {
  /** `scope='Agency-Private'` → the audit-lead view (own agency only); omit for the
   *  GRC-team view (GRC-Common). */
  async list(scope?: string): Promise<ProcedureTemplate[]> {
    // Use /all (entity-scoped, NO pagination cap) so the program-builder
    // procedure picker and name lookups never miss templates. The capped
    // `?limit=100` variant silently dropped procedures on entities with >100
    // templates — causing the link-procedure "No results found" / "can't link
    // latest procedures" bug.
    const resp = await apiClient.get(`${BASE}/procedure_templates/all`, { params: scope ? { scope } : undefined });
    return unwrap<ProcedureTemplate>(resp.data);
  },
  /** Server-side paginated list — used by AuditProcedures table. */
  async listPaged(opts: ProcedureTemplateListParams): Promise<PagedResult<ProcedureTemplate>> {
    const params: Record<string, any> = { page: opts.page, limit: opts.limit };
    const s = opts.search?.trim();
    if (s)          params.search = s;
    if (opts.type)  params.procedure_type = opts.type;
    if (opts.scope) params.ownership_scope = opts.scope;
    if (opts.status && opts.status !== 'ALL') params.status = opts.status;
    const resp = await apiClient.get(`${BASE}/procedure_templates`, { params });
    const data = resp.data ?? {};
    return {
      items: Array.isArray(data.data) ? data.data : [],
      total: data.meta?.total ?? 0,
    };
  },
  /** BRD#859: active-only list for program-builder picker */
  async listActive(): Promise<ProcedureTemplate[]> {
    const resp = await apiClient.get(`${BASE}/procedure_templates/active`);
    return Array.isArray(resp.data) ? resp.data : [];
  },
  /** Tenant-scoped server-side search — filters by entity_id automatically. */
  async lookup(search: string): Promise<ProcedureTemplate[]> {
    const resp = await apiClient.get(`${BASE}/procedure_templates/lookup`, { params: { search } });
    return Array.isArray(resp.data) ? resp.data : [];
  },
  async get(id: number): Promise<ProcedureTemplate> {
    const resp = await apiClient.get(`${BASE}/procedure_templates/${id}`);
    return resp.data;
  },
  async create(payload: ProcedureTemplateCreatePayload): Promise<ProcedureTemplate> {
    const resp = await apiClient.post(`${BASE}/procedure_templates`, payload);
    return resp.data;
  },
  async update(id: number, payload: ProcedureTemplateUpdatePayload): Promise<ProcedureTemplate> {
    const resp = await apiClient.put(`${BASE}/procedure_templates/${id}`, payload);
    return resp.data;
  },
  /** BRD#858: no deletion — only inactivate */
  async inactivate(id: number): Promise<ProcedureTemplate> {
    const resp = await apiClient.patch(`${BASE}/procedure_templates/${id}/inactivate`);
    return resp.data;
  },
  async reactivate(id: number): Promise<ProcedureTemplate> {
    const resp = await apiClient.patch(`${BASE}/procedure_templates/${id}/reactivate`);
    return resp.data;
  },
  /** Read-only SOP context for Walkthrough-* procedure types. */
  async sopContext(id: number): Promise<SopWalkthroughContext> {
    const resp = await apiClient.get(`${BASE}/procedure_templates/${id}/sop-context`);
    return (resp.data?.data ?? { sop: null, steps: [], controls: [], risks: [] }) as SopWalkthroughContext;
  },
};

export interface SopWalkthroughSop {
  id: number;
  process_name: string;
  description?: string | null;
  owner?: string | null;
  version?: string | null;
  process_status?: string | null;
  effective_date?: string | null;
  review_cycle_months?: number | null;
  is_active?: number;
}

/** Test-of-Design cascade: a control mitigating a step, with the risks it
 *  addresses and its implementation steps. Populated on step.controls. */
export interface SopWalkthroughStepRisk {
  risk_id: number;
  risk_code: string;
  risk_name: string;
}
export interface SopWalkthroughStepImpl {
  id: number;
  sequence_number?: number | null;
  implementation_type?: string | null;
  explanation?: string | null;
}
export interface SopWalkthroughStepControl {
  control_id: number;
  control_code: string;
  control_name: string;
  control_type?: string | null;
  risks: SopWalkthroughStepRisk[];
  implementations: SopWalkthroughStepImpl[];
}

export interface SopWalkthroughStep {
  id: number;
  step_number: number;
  step_name: string;
  description?: string | null;
  step_type?: string | null;
  /** Per-step mitigating controls → risks + implementations (Test of Design). */
  controls?: SopWalkthroughStepControl[];
}

export interface SopWalkthroughControl {
  id: number;
  control_code: string;
  control_name: string;
  control_type?: string | null;
  category_id?: number | null;
}

export interface SopWalkthroughRisk {
  id: number;
  risk_code: string;
  risk_name: string;
  category_id?: number | null;
}

export interface SopWalkthroughContext {
  sop: SopWalkthroughSop | null;
  steps: SopWalkthroughStep[];
  controls: SopWalkthroughControl[];
  risks: SopWalkthroughRisk[];
}

// --- Program templates ---
export const programTemplateService = {
  /** `scope='Agency-Private'` → the audit-lead view (own agency only); omit for the
   *  GRC-team view (GRC-Common). */
  async list(scope?: string): Promise<ProgramTemplate[]> {
    const resp = await apiClient.get(`${BASE}/program_templates`, { params: { limit: 100, sort: 'id DESC', ...(scope ? { scope } : {}) } });
    return unwrap<ProgramTemplate>(resp.data);
  },
  async create(payload: ProgramTemplateCreatePayload): Promise<ProgramTemplate> {
    const resp = await apiClient.post(`${BASE}/program_templates`, payload);
    return resp.data;
  },
  async update(id: number, payload: ProgramTemplateUpdatePayload): Promise<ProgramTemplate> {
    const resp = await apiClient.put(`${BASE}/program_templates/${id}`, payload);
    return resp.data;
  },
  /** BRD#914: templates are never deleted — only inactivated */
  async inactivate(id: number): Promise<void> {
    await apiClient.patch(`${BASE}/program_templates/${id}/inactivate`);
  },
  async reactivate(id: number): Promise<void> {
    await apiClient.patch(`${BASE}/program_templates/${id}/reactivate`);
  },
  /** Dedicated endpoint — sets ADT_program_templates.agency_id = <agencyId>. */
  async setAgency(id: number, agencyId: number): Promise<ProgramTemplate> {
    const url = `${BASE}/program_templates/${id}/set-agency`;
    console.log('[programTemplateService.setAgency] PATCH', url, {
      program_id: id, agency_id: agencyId, table: 'ADT_program_templates', column: 'agency_id',
    });
    const resp = await apiClient.patch(url, null, { params: { agency_id: agencyId } });
    console.log('[programTemplateService.setAgency] response', resp.status, resp.data);
    return resp.data;
  },
  /** Dedicated endpoint — sets ADT_program_templates.agency_id = NULL. */
  async unlinkAgency(id: number): Promise<ProgramTemplate> {
    const url = `${BASE}/program_templates/${id}/unlink-agency`;
    console.log('[programTemplateService.unlinkAgency] PATCH', url, {
      program_id: id, table: 'ADT_program_templates', column: 'agency_id -> NULL',
    });
    const resp = await apiClient.patch(url);
    console.log('[programTemplateService.unlinkAgency] response', resp.status, resp.data);
    return resp.data;
  },
  /** Plan 04-06 / DRC-04: consume phase 03 nested-procedures endpoint. */
  async getWithProcedures(id: number): Promise<{
    program: ProgramTemplate;
    procedures: Array<ProcedureTemplate & { sequence_no: number | null; map_id?: number }>;
  }> {
    const resp = await apiClient.get(`${BASE}/program_templates/${id}/with-procedures`);
    return resp.data;
  },
};

// ============================================================================
// Plan 04b-03 / DRC-07 — Program Instance launch (cascade endpoint client).
// Consumes POST /api/v1/adt/program_instances/launch-from-template.
// ============================================================================

/**
 * Locked role set for client-side Launch button gating.
 * Mirrors backend `_check_program_launch_auth` (plan 04b-02).
 */
const LAUNCH_GLOBAL_ROLES: ReadonlySet<string> = new Set([
  'Super Admin',
  'GRC Audit Head',
  'GRC Staffing Team',
]);

/** Minimal user shape required by `isLaunchable`. Compatible with the auth-context user shape. */
export interface LaunchUser {
  id: number;
  roles?: string[] | null;
  agency_id?: number | null;
}

/** Minimal template shape required by `isLaunchable`. */
export interface LaunchTemplate {
  id: number;
  agency_id?: number | null;
}

/**
 * DRC-07 / CONTEXT D4 — client-side mirror of backend `_check_program_launch_auth`.
 * Returns true iff the user can launch this template. Used by AuditPrograms.tsx
 * (plan 04b-04) to hide the Launch button when the user lacks authority.
 *
 * Note: This is a UX convenience only — the backend remains the authoritative gate.
 */
export function isLaunchable(
  template: LaunchTemplate | null | undefined,
  user: LaunchUser | null | undefined,
): boolean {
  if (!template || !user) return false;
  const roles = user.roles ?? [];
  if (roles.some((r) => LAUNCH_GLOBAL_ROLES.has(r))) return true;
  if (roles.includes('Agency Head')) {
    // Agency Head may launch only agency-private templates from THEIR agency.
    return template.agency_id != null && template.agency_id === user.agency_id;
  }
  return false;
}

/** Request body for POST /program_instances/launch-from-template (mirrors plan 04b-02 contract). */
export interface ProgramLaunchRequest {
  program_template_id: number;
  start_date: string; // YYYY-MM-DD
  due_date: string;   // YYYY-MM-DD
  responsible_user_id?: number | null;
  accountable_user_id?: number | null;   // the Program Lead
  approver_user_id?: number | null;
  agency_id?: number | null;             // optional agency to attach at launch
  audit_program_code?: string | null;
  audit_program_description?: string | null;
}

/**
 * Response body. `program_instance` is intentionally typed loosely until a canonical
 * ProgramInstance interface is introduced; the v1 modal only consumes `.id` and a toast.
 */
export interface ProgramLaunchResponse {
  program_instance: Record<string, unknown> & { id: number };
  procedure_instances: ProcedureInstance[];
  launched_at: string;
  launched_by: number;
}

export const programInstanceService = {
  /**
   * DRC-07 — Launch a program template into a live run.
   * Returns the created program_instance + N procedure_instances in a single cascade response.
   */
  async launch(payload: ProgramLaunchRequest): Promise<ProgramLaunchResponse> {
    const resp = await apiClient.post(
      `${BASE}/program_instances/launch-from-template`,
      payload,
    );
    return resp.data;
  },
  /**
   * 2026-05-18 (migration 081) — release the audit report. Allowed only when
   * the program is Approved and the caller is the approver or an Agency Head
   * of the program's agency. Flips the visibility gate that hides findings
   * from the auditee side until the report is formally released.
   */
  async release(programInstanceId: number): Promise<any> {
    const resp = await apiClient.post(
      `${BASE}/program_instances/${programInstanceId}/release`,
      {},
    );
    return resp.data;
  },
  async get(programInstanceId: number): Promise<any> {
    const resp = await apiClient.get(`${BASE}/program_instances/${programInstanceId}`);
    return resp.data;
  },
  /** Phase 08 — Reports page: list programs current user can publish + finding counts. */
  async myReports(): Promise<{ data: ProgramReportRow[]; meta: { total: number } }> {
    const resp = await apiClient.get(`${BASE}/program_instances/my-reports`);
    return resp.data;
  },
  /** Workflow transition — Draft → Submitted. */
  async submit(programInstanceId: number, comment?: string): Promise<any> {
    const resp = await apiClient.post(
      `${BASE}/program_instances/${programInstanceId}/submit`,
      comment ? { comment } : {},
    );
    return resp.data;
  },
  /** Workflow transition — Submitted → InReview. */
  async startReview(programInstanceId: number, comment?: string): Promise<any> {
    const resp = await apiClient.post(
      `${BASE}/program_instances/${programInstanceId}/start-review`,
      comment ? { comment } : {},
    );
    return resp.data;
  },
  /** Workflow transition — InReview → Approved. */
  async approve(programInstanceId: number, comment?: string): Promise<any> {
    const resp = await apiClient.post(
      `${BASE}/program_instances/${programInstanceId}/approve`,
      comment ? { comment } : {},
    );
    return resp.data;
  },
  /** Workflow transition — Submitted/InReview → Rejected. */
  async reject(programInstanceId: number, comment: string): Promise<any> {
    const resp = await apiClient.post(
      `${BASE}/program_instances/${programInstanceId}/reject`,
      { comment },
    );
    return resp.data;
  },
};

// ============================================================================
// --- Program Instance type + Kanban services (Phase 106 / plan 05) ---
// D-07 program lifecycle states: Launched | Allocated | Active |
// PendingApproval | DueForRelease | Released.
// listProgramInstances: entity-scoped program list (Agency-Head filtered server-side).
// allocateProgramLead: POST /program_instances/{id}/allocate-lead (T-106-15).
// listAgencyMembers: members of a given agency (picker pool for PL allocation).
// ============================================================================

/** Full program instance record returned by the backend. */
export interface ProgramInstance {
  id: number;
  entity_id?: number | null;
  audit_program_code: string;
  audit_program_description?: string | null;
  program_template_id?: number | null;
  start_date?: string | null;
  due_date?: string | null;
  responsible_user_id?: number | null;
  accountable_user_id?: number | null;
  approver_user_id?: number | null;
  program_lead_user_id?: number | null;
  agency_id?: number | null;
  workflow_state: WorkflowState | string;
  status?: string | null;
  is_active?: number;
  launched_at?: string | null;
  launched_by?: number | null;
  created_by?: number | null;
  created_on?: string | null;
  modified_by?: number | null;
  modified_on?: string | null;
  /** LEFT-JOIN denormalized (may be null if template soft-deleted — D-11) */
  program_name?: string | null;
  agency_name?: string | null;
  lead_name?: string | null;
  /** Audit-Lead kanban card counter: approved vs total active procedure tasks. */
  approved_task_count?: number | null;
  total_task_count?: number | null;
}

/** Agency member with user name columns (JOIN from USR_users). */
export interface AgencyMemberUser {
  id: number;
  agency_id: number;
  user_id: number;
  member_role?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  login_id?: string | null;
  is_active?: number;
}

export const programKanbanService = {
  /**
   * Phase 106 / plan 05 — list all program instances for this entity (entity-scoped).
   * Backend filters by caller's entity_id; Agency Head sees only their agency's programs.
   */
  async listProgramInstances(): Promise<ProgramInstance[]> {
    const resp = await apiClient.get(`${BASE}/program_instances`, {
      // Agency Head tab — backend scopes to programs of agencies the caller heads.
      params: { agency_head_user_id: 'me', limit: 200 },
    });
    const data = resp.data;
    if (Array.isArray(data)) return data as ProgramInstance[];
    if (data && Array.isArray(data.data)) return data.data as ProgramInstance[];
    return [];
  },

  /**
   * Phase 106 / plan 05 — allocate a Program Lead to a Launched program.
   * POST /program_instances/{id}/allocate-lead { user_id }
   * Drives Launched -> Allocated via D-07 state machine (T-106-15).
   */
  async allocateProgramLead(programId: number, userId: number): Promise<ProgramInstance> {
    const resp = await apiClient.post(
      `${BASE}/program_instances/${programId}/allocate-lead`,
      { user_id: userId },
    );
    return resp.data as ProgramInstance;
  },

  /**
   * Phase 106 / plan 05 — list members of a given agency (picker pool for PL allocation).
   * Reuses the existing agencyMemberService endpoint, shaped for the Kanban picker.
   */
  async listAgencyMembers(agencyId: number): Promise<AgencyMemberUser[]> {
    // Agency-scoped + JOINed with USR_users so the picker shows real names
    // (first_name/last_name/login_id) for THIS agency only — the generic
    // /agency_members endpoint ignores agency_id and returns no names.
    const resp = await apiClient.get(`${BASE}/agencies/${agencyId}/members`);
    const data = resp.data;
    if (Array.isArray(data)) return data as AgencyMemberUser[];
    if (data && Array.isArray(data.data)) return data.data as AgencyMemberUser[];
    return [];
  },
};

export interface ProgramReportRow {
  id: number;
  audit_program_code: string;
  workflow_state: string;
  released_at: string | null;
  released_by: number | null;
  due_date: string | null;
  start_date: string | null;
  modified_on: string | null;
  program_name: string | null;
  agency_name: string | null;
  lead_name: string | null;
  approver_name: string | null;
  finding_count: number;
  open_finding_count: number;
  material_finding_count: number;
  total_raised_count: number;
  can_publish: boolean;
}

// --- Program <-> procedure mapping ---
export const programProcedureMapService = {
  async list(): Promise<ProgramProcedureMap[]> {
    // Use /all (entity-scoped, NO pagination cap). The capped `?limit=100`
    // variant counts maps across ALL programs in the entity, so a program's
    // links beyond the global cap silently vanished from Program Configuration
    // (Procedures tab showed only a subset of linked procedures).
    const resp = await apiClient.get(`${BASE}/program_procedure_maps/all`);
    return unwrap<ProgramProcedureMap>(resp.data);
  },
  async create(payload: { program_template_id: number; procedure_template_id: number; sequence_no?: number; entity_id?: number; created_by?: number }): Promise<ProgramProcedureMap> {
    const resp = await apiClient.post(`${BASE}/program_procedure_maps`, payload);
    return resp.data;
  },
  /** BRD#918: update sequence number for reordering */
  async updateSequence(id: number, sequence_no: number): Promise<ProgramProcedureMap> {
    const resp = await apiClient.put(`${BASE}/program_procedure_maps/${id}`, { sequence_no });
    return resp.data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/program_procedure_maps/${id}`);
  },
};

// --- Pickers used by Walkthrough / RiskAssessment / ControlTest configurators ---
//
// Each one points back at a single source-of-truth template kept in another
// module. We don't duplicate that data — just store the foreign-key on
// ADT_procedure_templates (linked_sop_id, linked_risk_assessment_template_id,
// linked_control_test_template_id).

export const sopPickerService = {
  // Auditor-facing SOP picker (Walkthrough procedure configurator). Must show ONLY
  // audit-enabled SOPs (UAT-36). "audit-enabled" = the GRC team toggled an Audit
  // procedure visibility checkbox, which the backend audit_only filter resolves via
  // the presence of an active WalkthroughDesign/WalkthroughCompliance template.
  async list(): Promise<SOPProcess[]> {
    const resp = await apiClient.get('/api/v1/gov/processes', { params: { limit: 100, audit_only: true } });
    // Approved + active only — GOV_processes gate is process_status='APPROVED'
    // (mirrors /gov/processes/dropdown). Draft/unapproved SOPs must not appear.
    return unwrap<SOPProcess>(resp.data)
      .filter(s => (s.is_active ?? 1) === 1 && s.process_status === 'APPROVED');
  },
};

export const riskAssessmentTemplatePickerService = {
  async list(): Promise<RiskAssessmentTemplate[]> {
    const resp = await apiClient.get('/api/v1/rsk/assessment-templates', { params: { limit: 100 } });
    return unwrap<RiskAssessmentTemplate>(resp.data).filter(t => (t.is_active ?? 1) !== -1);
  },
};

export const controlTestTemplatePickerService = {
  async list(): Promise<ControlTestTemplate[]> {
    const resp = await apiClient.get('/api/v1/ctr/test-templates', { params: { limit: 100 } });
    return unwrap<ControlTestTemplate>(resp.data).filter(t => (t.is_active ?? 1) !== -1);
  },
};

// --- Questionnaire sub-template (per-procedure open-text questions) ---
export const questionnaireTemplateService = {
  async listForProcedure(procedureTemplateId: number): Promise<QuestionnaireTemplateItem[]> {
    const resp = await apiClient.get(`${BASE}/questionnaire_templates`, { params: { limit: 100 } });
    const all = unwrap<QuestionnaireTemplateItem>(resp.data);
    return all.filter(q => q.procedure_template_id === procedureTemplateId && q.is_active !== -1);
  },
  async create(payload: { procedure_template_id: number; question_text: string; evidence_required?: boolean; is_active?: number }): Promise<QuestionnaireTemplateItem> {
    const resp = await apiClient.post(`${BASE}/questionnaire_templates`, payload);
    return resp.data;
  },
  async update(id: number, payload: Partial<QuestionnaireTemplateItem>): Promise<QuestionnaireTemplateItem> {
    const resp = await apiClient.put(`${BASE}/questionnaire_templates/${id}`, payload);
    return resp.data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/questionnaire_templates/${id}`);
  },
};

// --- Form sub-template (per-procedure typed fields) ---
export const formTemplateService = {
  async listForProcedure(procedureTemplateId: number): Promise<FormTemplateItem[]> {
    const resp = await apiClient.get(`${BASE}/form-templates`, { params: { limit: 100 } });
    const all = unwrap<FormTemplateItem>(resp.data);
    return all.filter(f => f.procedure_template_id === procedureTemplateId && f.is_active !== -1);
  },
  async create(payload: Partial<FormTemplateItem> & { procedure_template_id: number; form_question: string; answer_data_type: string }): Promise<FormTemplateItem> {
    const resp = await apiClient.post(`${BASE}/form-templates`, payload);
    return resp.data;
  },
  async update(id: number, payload: Partial<FormTemplateItem>): Promise<FormTemplateItem> {
    const resp = await apiClient.put(`${BASE}/form-templates/${id}`, payload);
    return resp.data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/form-templates/${id}`);
  },
};

// --- Confirmation scoping rows (per procedure) ---
export const confirmationTemplateService = {
  async listForProcedure(procedureTemplateId: number): Promise<ConfirmationTemplate[]> {
    const resp = await apiClient.get(`${BASE}/confirmation_templates`, { params: { limit: 100 } });
    const all = unwrap<ConfirmationTemplate>(resp.data);
    return all.filter(c => c.procedure_template_id === procedureTemplateId && c.is_active !== -1);
  },
  async create(payload: Partial<ConfirmationTemplate> & { procedure_template_id: number }): Promise<ConfirmationTemplate> {
    const resp = await apiClient.post(`${BASE}/confirmation_templates`, payload);
    return resp.data;
  },
  async update(id: number, payload: Partial<ConfirmationTemplate>): Promise<ConfirmationTemplate> {
    const resp = await apiClient.put(`${BASE}/confirmation_templates/${id}`, payload);
    return resp.data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/confirmation_templates/${id}`);
  },
};

// --- Physical-check scoping rows (per procedure) ---
export const physicalCheckTemplateService = {
  async listForProcedure(procedureTemplateId: number): Promise<PhysicalCheckTemplate[]> {
    const resp = await apiClient.get(`${BASE}/physical_check_templates`, { params: { limit: 100 } });
    const all = unwrap<PhysicalCheckTemplate>(resp.data);
    return all.filter(p => p.procedure_template_id === procedureTemplateId && p.is_active !== -1);
  },
  async create(payload: Partial<PhysicalCheckTemplate> & { procedure_template_id: number }): Promise<PhysicalCheckTemplate> {
    const resp = await apiClient.post(`${BASE}/physical_check_templates`, payload);
    return resp.data;
  },
  async update(id: number, payload: Partial<PhysicalCheckTemplate>): Promise<PhysicalCheckTemplate> {
    const resp = await apiClient.put(`${BASE}/physical_check_templates/${id}`, payload);
    return resp.data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/physical_check_templates/${id}`);
  },
};

// --- Rating sub-template + options (questions and their scale options) ---
export const ratingTemplateService = {
  async listForProcedure(procedureTemplateId: number): Promise<RatingTemplateItem[]> {
    const resp = await apiClient.get(`${BASE}/rating-templates`, { params: { limit: 100 } });
    const all = unwrap<RatingTemplateItem>(resp.data);
    return all.filter(r => r.procedure_template_id === procedureTemplateId && r.is_active !== -1);
  },
  async create(payload: { procedure_template_id: number; rating_question: string; maximum_score?: number; is_normalized?: boolean; is_active?: number }): Promise<RatingTemplateItem> {
    const resp = await apiClient.post(`${BASE}/rating-templates`, payload);
    return resp.data;
  },
  async update(id: number, payload: Partial<RatingTemplateItem>): Promise<RatingTemplateItem> {
    const resp = await apiClient.put(`${BASE}/rating-templates/${id}`, payload);
    return resp.data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/rating-templates/${id}`);
  },
};

export const ratingOptionService = {
  async listForRating(ratingTemplateId: number): Promise<RatingOptionItem[]> {
    const resp = await apiClient.get(`${BASE}/rating-options-templates`, { params: { limit: 100 } });
    const all = unwrap<RatingOptionItem>(resp.data);
    return all.filter(o => o.rating_template_id === ratingTemplateId && o.is_active !== -1);
  },
  async listAll(): Promise<RatingOptionItem[]> {
    const resp = await apiClient.get(`${BASE}/rating-options-templates`, { params: { limit: 100 } });
    return unwrap<RatingOptionItem>(resp.data).filter(o => o.is_active !== -1);
  },
  async create(payload: { rating_template_id: number; rating_option_text: string; rating_option_score: number; is_active?: number }): Promise<RatingOptionItem> {
    const resp = await apiClient.post(`${BASE}/rating-options-templates`, payload);
    return resp.data;
  },
  async update(id: number, payload: Partial<RatingOptionItem>): Promise<RatingOptionItem> {
    const resp = await apiClient.put(`${BASE}/rating-options-templates/${id}`, payload);
    return resp.data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/rating-options-templates/${id}`);
  },
};

// --- Checklist sub-template (per-procedure rows of yes/no/na questions) ---
export const checklistTemplateService = {
  async listForProcedure(procedureTemplateId: number): Promise<ChecklistTemplateItem[]> {
    const resp = await apiClient.get(`${BASE}/checklist_templates`, { params: { limit: 100 } });
    const all = unwrap<ChecklistTemplateItem>(resp.data);
    return all.filter(c => c.procedure_template_id === procedureTemplateId && c.is_active !== -1);
  },
  async create(payload: ChecklistTemplateCreate): Promise<ChecklistTemplateItem> {
    const resp = await apiClient.post(`${BASE}/checklist_templates`, payload);
    return resp.data;
  },
  async update(id: number, payload: Partial<ChecklistTemplateItem>): Promise<ChecklistTemplateItem> {
    const resp = await apiClient.put(`${BASE}/checklist_templates/${id}`, payload);
    return resp.data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/checklist_templates/${id}`);
  },
};

// --- Procedure instances (live data for Schedule tab — Plan 04-04 / DRC-01) ---
// Backend endpoint: GET /api/v1/adt/procedure_instances
// Returns paginated rows with B1 LEFT-JOIN denormalized name columns
// (program_name, agency_name, lead_name) added by plan 04-01. The UI
// renders them directly — NO client-side join or extra fetches.
export type WorkflowState =
  | 'Draft'
  | 'Submitted'
  | 'InReview'
  | 'Approved'
  | 'Rejected'
  | 'Reopened'
  // Phase 06 (FIND-01..04) — finding lifecycle states. Derived from the
  // ADT_findings columns is_closed + responded_at by deriveState() in
  // FindingDetailModal. The string keys are reused as AuditStatusBadge
  // state pill labels (D2 + D3).
  | 'Raised'
  | 'Responded'
  | 'Closed'
  // Phase 106 / plan 05 — D-07 program lifecycle states.
  // AuditStatusBadge extension contract: new keys added here + WORKFLOW_STATE_STYLE
  // together; unknown keys gracefully render via FALLBACK style.
  | 'Launched'
  | 'Allocated'
  | 'Active'
  | 'PendingApproval'
  | 'DueForRelease'
  | 'Released'
  | 'InProgress';

export const WORKFLOW_STATES: WorkflowState[] = [
  'Draft', 'Submitted', 'InReview', 'Approved', 'Rejected', 'Reopened',
];

export const DEFAULT_SCHEDULE_WORKFLOW_STATES: WorkflowState[] = [
  'Draft', 'Submitted', 'InReview', 'Reopened',
];

export interface ProcedureInstance {
  id: number;
  entity_id?: number | null;
  audit_procedure_name: string;
  procedure_template_id?: number | null;
  audit_program_instance_id: number;
  start_date?: string | null;
  due_date?: string | null;
  responsible_user_id?: number | null;
  accountable_user_id?: number | null;
  approver_user_id?: number | null;
  workflow_state: WorkflowState;
  status?: string | null;
  is_active?: number;

  // B1 (plan 04-01) — backend LEFT-JOIN denormalized name columns.
  // Null when parent row is missing; UI renders '—' in that case.
  program_name?: string | null;
  agency_name?: string | null;
  lead_name?: string | null;
}

export interface ScheduleListParams {
  workflow_state?: WorkflowState[];
  due_from?: string;     // YYYY-MM-DD
  due_to?: string;       // YYYY-MM-DD
  page: number;
  limit: number;
}

export const scheduleService = {
  async list(opts: ScheduleListParams): Promise<PagedResult<ProcedureInstance>> {
    const params: Record<string, any> = {
      page: opts.page,
      limit: opts.limit,
      // D2 lock — explicit sort=due_date so the Schedule UI is ordered by
      // due-date ASC even though the backend default is "id" (W6 lock).
      sort: 'due_date',
      order: 'asc',
    };
    if (opts.workflow_state && opts.workflow_state.length > 0) {
      params.workflow_state = opts.workflow_state.join(',');
    }
    if (opts.due_from) params.due_from = opts.due_from;
    if (opts.due_to)   params.due_to   = opts.due_to;
    const resp = await apiClient.get(`${BASE}/procedure_instances`, { params });
    const data = resp.data ?? {};
    return {
      items: Array.isArray(data.data) ? data.data : [],
      total: data.meta?.total ?? 0,
    };
  },
};

// --- Findings (live data for Findings tab — Plan 04-05 / DRC-02) ---
// Backend endpoint: GET /api/v1/adt/findings
// Plan 04-01 extended it to accept the three Decision-D3 filter params
// (is_trivial, is_closed, raised_on_date_from, raised_on_date_to).
export interface Finding {
  id: number;
  entity_id?: number | null;
  procedure_instance_id: number;
  item_type_id?: number | null;
  item_record_id?: number | null;
  code?: string | null;
  findings_text: string;
  raised_on_date?: string | null;
  response_user_id?: number | null;
  is_trivial?: boolean;
  is_closed?: boolean;
  is_active?: number;
  created_by?: number | null;
  created_on?: string | null;
  /** Phase 06 lifecycle columns (migration 076). */
  response_text?: string | null;
  responded_at?: string | null;
  closed_at?: string | null;
  closed_by?: number | null;
  /** Phase 08 allocation columns (migration 083). */
  allocated_to_user_id?: number | null;
  allocated_at?: string | null;
  allocated_by?: number | null;
  allocation_due_date?: string | null;
  allocation_note?: string | null;
  /** Phase 08 push-to-report columns (migration 084). */
  pushed_to_report_at?: string | null;
  pushed_to_report_by?: number | null;
  /** Denormalized LEFT-JOIN fields surfaced by /findings + /findings/allocated-to-me. */
  procedure_name?: string | null;
  program_name?: string | null;
  allocated_by_name?: string | null;
  program_released_at?: string | null;
}

export interface FindingsListParams {
  is_trivial?: boolean;       // undefined = both
  is_closed?: boolean;        // undefined = both
  raised_on_date_from?: string;
  raised_on_date_to?: string;
  /** Phase 08 — filter to findings under a specific program instance (Reports page). */
  program_instance_id?: number;
  /** Phase 08 — filter to findings under a specific procedure instance. */
  procedure_instance_id?: number;
  page: number;
  limit: number;
}

export const findingsService = {
  async list(opts: FindingsListParams): Promise<PagedResult<Finding>> {
    const params: Record<string, any> = {
      page: opts.page,
      limit: opts.limit,
      sort: 'raised_on_date',
    };
    if (opts.is_trivial !== undefined) params.is_trivial = opts.is_trivial;
    if (opts.is_closed  !== undefined) params.is_closed  = opts.is_closed;
    if (opts.raised_on_date_from)      params.raised_on_date_from = opts.raised_on_date_from;
    if (opts.raised_on_date_to)        params.raised_on_date_to   = opts.raised_on_date_to;
    if (opts.program_instance_id != null)   params.program_instance_id   = opts.program_instance_id;
    if (opts.procedure_instance_id != null) params.procedure_instance_id = opts.procedure_instance_id;
    const resp = await apiClient.get(`${BASE}/findings`, { params });
    const data = resp.data ?? {};
    return {
      items: Array.isArray(data.data) ? data.data : [],
      total: data.meta?.total ?? 0,
    };
  },
};

// --- Insights (Plan 04-03; consumes backend plan 04-01) ---
// DRC AuditView → Insights tab. Single-call aggregation endpoint shipped by
// plan 04-01 (D1 lock — 4 tiles, server-side SQL, no per-tile fan-out).
//
// Response shape mirrors backend `ADTInsightsResponse` (backend/models/adt_schemas.py):
//   programs_by_status  Record<workflow_state, count>   (6 keys, zero-filled)
//   open_findings       { total, material, trivial }     where total = material + trivial
//   due_this_week       count of in-flight procedure_instances due in [today, today+7d]
//   overdue             count using B5-aligned _IS_OVERDUE_SQL predicate
//   meta                { entity_id, generated_at (ISO-8601 UTC) }
export interface AuditInsightsResponse {
  programs_by_status: Record<string, number>;
  /** 4-phase rollup: Planning / Active / In Review / Closed. */
  programs_by_phase: Record<string, number>;
  total_programs: number;
  /** Procedure execution pipeline, keyed by procedure workflow_state. */
  procedures_by_state: Record<string, number>;
  /** Findings open/closed split ({ open, closed, total }). */
  findings_by_status: { open: number; closed: number; total: number };
  /** Procedures scheduled to start within the next 30 days. */
  upcoming_30d: number;
  open_findings: { total: number; material: number; trivial: number };
  due_this_week: number;
  overdue: number;
  meta: { entity_id: number; generated_at: string };
}

export const insightsService = {
  async get(): Promise<AuditInsightsResponse> {
    const resp = await apiClient.get(`${BASE}/insights`);
    return resp.data;
  },
};

// --- My Work (Plan 05a-02 / STAFF-02) ---
// Backend endpoint: GET /api/v1/adt/my-work
// Plan 05a-02 extended the bucket rows with LEFT-JOIN denormalized parent
// template fields (program_name, procedure_name, procedure_type, days_remaining)
// so the dashboard renders each card without extra fetches.

// Plan 05b-06 / STAFF-07 — 'my_approvals' is the 4th bucket. Surfaces program +
// procedure instances where current_user.id == approver_user_id AND
// workflow_state IN ('Submitted','InReview'). Backend: section 7d of adt_routes.
export type MyWorkBucketName = 'due' | 'due_for_release' | 'active' | 'my_approvals' | 'my_approved';

export type MyWorkInstanceType = 'program' | 'procedure';

export interface MyWorkRow {
  id: number;
  instance_type: MyWorkInstanceType;
  workflow_state: WorkflowState;
  due_date: string | null;          // YYYY-MM-DD
  responsible_user_id: number | null;
  approver_user_id: number | null;
  accountable_user_id: number | null;
  /** Program code (for instance_type='program') OR procedure name (for 'procedure'). Always populated. */
  label: string | null;
  parent_program_instance_id: number | null;
  is_overdue: boolean;
  // Plan 05a-02 denormalized fields:
  program_name: string | null;       // parent program template name
  procedure_name: string | null;     // procedure_instances.audit_procedure_name (null for program rows)
  procedure_type: string | null;     // procedure_templates.procedure_type (null for program rows)
  days_remaining: number | null;     // (due_date - today) in days; negative = overdue; null when due_date is NULL
  /** Populated only for sample-child procedure instances (spawned via spawn-sample-instances). */
  sample_label: string | null;
}

export interface MyWorkMeta {
  scope: 'mine' | 'program' | 'agency';
  window_days: number;
  include: MyWorkBucketName[];
}

export interface MyWorkResponse {
  due: MyWorkRow[];
  due_for_release: MyWorkRow[];
  active: MyWorkRow[];
  /** Plan 05b-06 / STAFF-07 — approver discovery surface. Always present in the response. */
  my_approvals: MyWorkRow[];
  /** bug-13 — approver outcome surface (Approved/Rejected by me). Present when requested. */
  my_approved?: MyWorkRow[];
  meta: MyWorkMeta;
}

export interface MyWorkParams {
  /** Optional override; default is role-derived on the backend. */
  scope?: 'mine' | 'program' | 'agency';
  /** Lookahead for the `due` bucket, in days. Default 7 on the backend. */
  window_days?: number;
  /** CSV subset — when omitted, all four buckets are returned. */
  include?: MyWorkBucketName[];
}

export const myWorkService = {
  async get(params: MyWorkParams = {}): Promise<MyWorkResponse> {
    const query: Record<string, any> = {};
    if (params.scope)        query.scope = params.scope;
    if (params.window_days !== undefined) query.window_days = params.window_days;
    if (params.include && params.include.length > 0) {
      query.include = params.include.join(',');
    }
    const resp = await apiClient.get(`${BASE}/my-work`, { params: query });
    return resp.data as MyWorkResponse;
  },
};

// --- My Procedures (dashboard secondary module, 2026-05-18) ---
// Backend endpoint: GET /api/v1/adt/my-procedures
// Flat list of every ADT_procedure_instance under the caller (responsible /
// approver / accountable), any workflow_state. Rendered as a separate section
// below the 4-bucket grid in AuditorDashboardContent so the user can see
// procedures regardless of bucket eligibility (e.g., Approved-but-no-finding
// procedures the bucket filters miss).

export type MyRole = 'performer' | 'approver' | 'lead' | 'other';

export interface MyProcedureRow extends MyWorkRow {
  /** Which seat the caller occupies on this procedure. */
  my_role: MyRole;
}

export interface MyProceduresResponse {
  data: MyProcedureRow[];
  meta: { total: number; user_id: number };
}

export const myProceduresService = {
  async list(opts: { include_inactive?: boolean } = {}): Promise<MyProceduresResponse> {
    const params: Record<string, any> = {};
    if (opts.include_inactive) params.include_inactive = true;
    const resp = await apiClient.get(`${BASE}/my-procedures`, { params });
    return resp.data as MyProceduresResponse;
  },
};

// --- My Findings (dashboard secondary module, 2026-05-18) ---
// Backend endpoint: GET /api/v1/adt/my-findings
// UNION of findings where caller is the raiser (created_by) OR the responder
// (response_user_id). The `relation` field distinguishes the two; rows where
// the caller is both raiser and responder carry relation='both'.

export type FindingRelation = 'raised' | 'to_respond' | 'both' | 'other';

export interface MyFinding extends Finding {
  relation: FindingRelation;
  procedure_name: string | null;
  audit_program_instance_id: number | null;
  program_name: string | null;
  procedure_type: string | null;
}

export interface MyFindingsResponse {
  data: MyFinding[];
  meta: { total: number; user_id: number };
}

export const myFindingsService = {
  async list(opts: { include_closed?: boolean } = {}): Promise<MyFindingsResponse> {
    const params: Record<string, any> = {};
    if (opts.include_closed === false) params.include_closed = false;
    const resp = await apiClient.get(`${BASE}/my-findings`, { params });
    return resp.data as MyFindingsResponse;
  },
};

// --- Assignment (Plan 05a-03 / STAFF-03) ---
// Backend endpoint: PUT /api/v1/adt/procedure_instances/{id}
// Plan 05a-03 wired the handler to accept approver_user_id (the column
// already existed per migration 071; only the FastAPI handler was missing the
// SET-list line). assignmentService is a thin typed wrapper used by the
// AssignmentModal that the dashboard mounts on the Assign button.

export interface AssignmentPayload {
  responsible_user_id?: number | null;
  approver_user_id?: number | null;
  due_date?: string | null;          // YYYY-MM-DD
  /** Required for the audit trail on every PUT. Caller supplies current_user.id. */
  modified_by: number;
}

export const assignmentService = {
  async update(procedureInstanceId: number, payload: AssignmentPayload): Promise<ProcedureInstance> {
    const resp = await apiClient.put(
      `${BASE}/procedure_instances/${procedureInstanceId}`,
      payload,
    );
    return resp.data as ProcedureInstance;
  },
};

// --- Agency staff picker (Plan 05a-03 / STAFF-03) ---
// Thin wrapper over staffService.list — returns ACTIVE Staff only (excludes
// inactivated members). Used by AssignmentModal to populate the
// performer / approver dropdowns scoped to the program's agency.

export interface AgencyStaffOption {
  user_id: number;
  member_id: number;
  full_name: string;
  email: string | null;
  role: string;
}

export const agencyStaffPickerService = {
  async list(agencyId: number): Promise<AgencyStaffOption[]> {
    const staff = await staffService.list(agencyId);
    return staff
      .filter((s) => (s.is_active ?? 1) === 1)
      .map((s) => ({
        user_id: s.user_id,
        member_id: s.member_id,
        full_name: `${s.first_name} ${s.last_name}`.trim() || (s.login_id ?? `User ${s.user_id}`),
        email: s.email ?? null,
        role: s.member_role ?? 'Staff',
      }));
  },
};

// ============================================================================
// 05b — Phase 05b execution layer service blocks (plan 05b-02).
// These services power the new procedure-responses/ family of components and
// the approval workflow UI. NO inline apiClient calls allowed outside this
// module — every 05b component goes through one of these exports.
// ============================================================================

// --- State transitions (Plan 05b-02 / STAFF-07) ---
// Backend section 7c — 10 endpoints, 5 verbs x 2 instance types.
// Comment is REQUIRED for `reject` (server returns 400 on empty).

export type StateTransitionInstanceType = 'program' | 'procedure';
export type StateTransitionVerb = 'submit' | 'start-review' | 'approve' | 'reject' | 'reopen' | 'release';

export interface StateTransitionResult {
  instance: ProcedureInstance | Record<string, any>;
  transition: {
    id: number;
    instance_type: StateTransitionInstanceType;
    instance_id: number;
    from_state: WorkflowState;
    to_state: WorkflowState;
    actor_user_id: number;
    comment: string | null;
    created_on: string | null;
  };
}

async function _postTransition(
  instanceType: StateTransitionInstanceType,
  verb: StateTransitionVerb,
  id: number,
  comment?: string | null,
): Promise<StateTransitionResult> {
  const url = `${BASE}/${instanceType === 'program' ? 'program_instances' : 'procedure_instances'}/${id}/${verb}`;
  const body: Record<string, any> = {};
  if (comment !== undefined && comment !== null) body.comment = comment;
  const resp = await apiClient.post(url, body);
  return resp.data as StateTransitionResult;
}

export const transitionService = {
  submit:      (t: StateTransitionInstanceType, id: number) => _postTransition(t, 'submit', id),
  startReview: (t: StateTransitionInstanceType, id: number) => _postTransition(t, 'start-review', id),
  approve:     (t: StateTransitionInstanceType, id: number) => _postTransition(t, 'approve', id),
  reject:      (t: StateTransitionInstanceType, id: number, comment: string) => {
    const c = (comment ?? '').trim();
    if (c.length === 0) {
      return Promise.reject(new Error('reject requires a non-empty comment.'));
    }
    return _postTransition(t, 'reject', id, c);
  },
  reopen:      (t: StateTransitionInstanceType, id: number) => _postTransition(t, 'reopen', id),
  /** D-07: program release — backend enforces "all tasks Approved" + one-shot. */
  release:     (id: number) => _postTransition('program', 'release', id),
};

// --- Procedure instance detail (Plan 05b-02) ---
// Single-row fetch for the ResponseShell header. The list endpoint already
// returns LEFT-JOIN denormalized fields (plan 04-01); this single-row variant
// returns the raw ADT_procedure_instances row — UI joins with the procedure
// template via the program-with-procedures nested endpoint when needed.

export const procedureInstanceService = {
  async get(id: number): Promise<ProcedureInstance> {
    const resp = await apiClient.get(`${BASE}/procedure_instances/${id}`);
    return resp.data as ProcedureInstance;
  },
  /** Soft-delete a procedure instance (used to remove a not-yet-started sample task). */
  async remove(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/procedure_instances/${id}`);
  },
};

// --- Responses (Plan 05b-02 / STAFF-05; extended Plan 05c-04 / STAFF-11) ---
// SIX backend response endpoints — Checklist, Questionnaire, Form, Rating,
// Confirmation, PhysicalCheck — shipped in 05b. THREE additional response
// endpoints — Walkthrough, RiskAssessment, ControlTest — shipped in 05c.
// The two Walkthrough sub-types (WalkthroughDesign / WalkthroughCompliance)
// remain deferred until the type is split into Walkthrough proper.

export interface ResponseUpsertPayload {
  [key: string]: any;
}

// IMPORTANT: URLs verified against backend/routes/adt_routes.py via the I2 grep
// precondition. Hyphen vs underscore is intentional and DB-driven — do not
// "normalize" without re-running the grep.
function _responseEndpoint(procedureType: string): string {
  switch (procedureType) {
    case 'Checklist':            return `${BASE}/checklist-responses`;
    case 'Questionnaire':        return `${BASE}/questionnaire_responses`;
    case 'Form':                 return `${BASE}/form_responses`;
    case 'Rating':               return `${BASE}/rating-responses`;
    case 'Confirmation':         return `${BASE}/confirmation_responses`;
    case 'PhysicalCheck':        return `${BASE}/physical_check_responses`;
    case 'Walkthrough':          return `${BASE}/walkthrough_responses`;
    case 'RiskAssessment':       return `${BASE}/risk_assessment_responses`;
    case 'ControlTest':          return `${BASE}/control_test_responses`;
    case 'WalkthroughDesign':
    case 'WalkthroughCompliance':
      throw new Error(
        `responseService: procedure_type "${procedureType}" is a deferred Walkthrough sub-type — ` +
        `the dispatcher should mount NotImplementedPlaceholder until the type is split into Walkthrough proper.`
      );
    default:
      throw new Error(`responseService: unsupported procedure_type "${procedureType}"`);
  }
}

export const responseService = {
  async list(procedureType: string, procedureInstanceId: number): Promise<any[]> {
    const url = _responseEndpoint(procedureType);
    // W4 — pass procedure_instance_id as a server filter param, not a client-side
    // filter on a 500-row dump.
    const resp = await apiClient.get(url, { params: { procedure_instance_id: procedureInstanceId, limit: 500 } });
    const data = resp.data?.data ?? resp.data ?? [];
    return Array.isArray(data) ? data : [];
  },
  async create(procedureType: string, payload: ResponseUpsertPayload): Promise<any> {
    const url = _responseEndpoint(procedureType);
    const resp = await apiClient.post(url, payload);
    return resp.data;
  },
  async update(procedureType: string, id: number, payload: ResponseUpsertPayload): Promise<any> {
    const url = `${_responseEndpoint(procedureType)}/${id}`;
    const resp = await apiClient.put(url, payload);
    return resp.data;
  },
};

// --- Rating responses (Plan 05b-03b / STAFF-05) ---
// Rating responses do NOT follow the standard /<table>?procedure_instance_id=N
// pattern that responseService.list uses. Backend ships two dedicated routes:
//
//   POST /adt/rating-responses/init/{procedure_instance_id}
//     -> Seeds one response row per active rating_template attached to the
//        procedure's procedure_template. Idempotent — skips rows that already
//        exist. Returns { message, count }.
//
//   GET  /adt/rating-responses/by-instance/{procedure_instance_id}
//     -> Lists all response rows for the instance.
//
//   PUT  /adt/rating-responses/{id}
//     -> Updates rating_option_id + score_obtained.
//
// RatingResponse.tsx uses this service block instead of responseService for
// list+init (the create step is server-side via init/). It still uses
// responseService.update('Rating', ...) for the per-row update because that
// path matches the standard PUT pattern.

export interface RatingResponseRow {
  id: number;
  entity_id?: number | null;
  procedure_instance_id: number;
  rating_template_id?: number | null;
  rating_option_id?: number | null;
  score_obtained?: number | null;
  is_active?: number;
}

export const ratingResponseService = {
  /** Seeds rating_responses for this instance from its template. Idempotent. */
  async init(procedureInstanceId: number): Promise<{ message: string; count: number }> {
    const resp = await apiClient.post(`${BASE}/rating-responses/init/${procedureInstanceId}`);
    return resp.data as { message: string; count: number };
  },
  /** Lists all rating responses for one procedure instance. */
  async listByInstance(procedureInstanceId: number): Promise<RatingResponseRow[]> {
    const resp = await apiClient.get(`${BASE}/rating-responses/by-instance/${procedureInstanceId}`);
    return Array.isArray(resp.data) ? (resp.data as RatingResponseRow[]) : [];
  },
};

// --- Walkthrough responses (Plan 05c-04 / STAFF-11) ---
// Sample-less process-audit response — one row per attached SOP step.
// Backend routes:
//   GET  /api/v1/adt/walkthrough_responses?procedure_instance_id=N
//   POST /api/v1/adt/walkthrough_responses
//   PUT  /api/v1/adt/walkthrough_responses/{id}
// Payload contract: process_step_id + status ('Complied'|'NotComplied'|'NA') + observation.

export interface WalkthroughResponseRow {
  id: number;
  entity_id: number | null;
  procedure_instance_id: number;
  process_step_id: number;
  status: 'Complied' | 'NotComplied' | 'NA' | 'Pass' | 'Fail';
  observation: string | null;
  is_active?: number;
  created_by?: number | null;
  created_on?: string | null;
  modified_by?: number | null;
  modified_on?: string | null;
}

export const walkthroughResponseService = {
  async list(procedureInstanceId: number): Promise<WalkthroughResponseRow[]> {
    const resp = await apiClient.get(`${BASE}/walkthrough_responses`, {
      params: { procedure_instance_id: procedureInstanceId, limit: 500 },
    });
    const data = resp.data?.data ?? resp.data ?? [];
    return Array.isArray(data) ? data : [];
  },
  async create(
    payload: Partial<WalkthroughResponseRow> & {
      procedure_instance_id: number;
      process_step_id: number;
      status: 'Complied' | 'NotComplied' | 'NA' | 'Pass' | 'Fail';
      created_by: number;
      entity_id: number;
    },
  ): Promise<WalkthroughResponseRow> {
    const resp = await apiClient.post(`${BASE}/walkthrough_responses`, payload);
    return resp.data as WalkthroughResponseRow;
  },
  async update(
    id: number,
    payload: Partial<WalkthroughResponseRow> & { modified_by: number },
  ): Promise<WalkthroughResponseRow> {
    const resp = await apiClient.put(`${BASE}/walkthrough_responses/${id}`, payload);
    return resp.data as WalkthroughResponseRow;
  },
};

// --- RiskAssessment responses (Plan 05c-04 / STAFF-11) ---
// Sample-less process-audit response — one row per attached risk.
// Backend routes:
//   GET  /api/v1/adt/risk_assessment_responses?procedure_instance_id=N
//   POST /api/v1/adt/risk_assessment_responses
//   PUT  /api/v1/adt/risk_assessment_responses/{id}
// NOTE: computed_score is server-set (likelihood * impact). Clients MUST NOT
// supply it on create/update — the Row interface includes it because the
// backend returns it; the create/update payload type omits it explicitly
// via RiskAssessmentResponseClientFields.

export interface RiskAssessmentResponseRow {
  id: number;
  entity_id: number | null;
  procedure_instance_id: number;
  risk_id: number;
  // Plan 111-01: nullable per-sub-process scoping. NULL = legacy RAT context;
  // set = this risk scored UNDER a specific SOP sub-process in a Test-of-Design body.
  process_step_id?: number | null;
  likelihood: number | null;        // 1..5
  impact: number | null;            // 1..5 (Severity)
  // Phase 110 — RISK-level control assessment (drives client-derived Residual).
  // Distinct from the per-control Ex/Ef in the nested mitigating-controls tree.
  existence_score: number | null;        // 1..5
  effectiveness_score: number | null;    // 1..5
  computed_score: number | null;    // 1..25 — server-set (inherent L×S)
  rationale: string | null;
  is_active?: number;
  created_by?: number | null;
  created_on?: string | null;
  modified_by?: number | null;
  modified_on?: string | null;
}

// Client-controlled fields only — computed_score is intentionally absent.
type RiskAssessmentResponseClientFields = Omit<RiskAssessmentResponseRow, 'computed_score'>;

export const riskAssessmentResponseService = {
  async list(procedureInstanceId: number): Promise<RiskAssessmentResponseRow[]> {
    const resp = await apiClient.get(`${BASE}/risk_assessment_responses`, {
      params: { procedure_instance_id: procedureInstanceId, limit: 500 },
    });
    const data = resp.data?.data ?? resp.data ?? [];
    return Array.isArray(data) ? data : [];
  },
  async create(
    payload: Partial<RiskAssessmentResponseClientFields> & {
      procedure_instance_id: number;
      risk_id: number;
      created_by: number;
      entity_id: number;
    },
  ): Promise<RiskAssessmentResponseRow> {
    const resp = await apiClient.post(`${BASE}/risk_assessment_responses`, payload);
    return resp.data as RiskAssessmentResponseRow;
  },
  async update(
    id: number,
    payload: Partial<RiskAssessmentResponseClientFields> & { modified_by: number },
  ): Promise<RiskAssessmentResponseRow> {
    const resp = await apiClient.put(`${BASE}/risk_assessment_responses/${id}`, payload);
    return resp.data as RiskAssessmentResponseRow;
  },
};

// --- ControlTest responses (Plan 05c-04 / STAFF-11) ---
// Sample-less process-audit response — one row per attached control.
// Backend routes:
//   GET  /api/v1/adt/control_test_responses?procedure_instance_id=N
//   POST /api/v1/adt/control_test_responses
//   PUT  /api/v1/adt/control_test_responses/{id}
// evidence_file_id references UTL_files.id; upload via fileService.upload
// first, then patch the id into the response on the next autosave.

export interface ControlTestResponseRow {
  id: number;
  entity_id: number | null;
  procedure_instance_id: number;
  control_id: number;
  // Plan 110-01: nullable per-risk scoping. NULL = legacy ControlTest/CAT context;
  // set = this control scored UNDER a specific risk in a RiskAssessment (RAT) body.
  risk_id?: number | null;
  // Plan 111-01: nullable per-sub-process scoping (Test of Design). NULL = legacy
  // RAT/CAT context; set = this control scored UNDER a specific SOP sub-process.
  process_step_id?: number | null;
  // Optional with parity model B (control-level result is derived from per-impl results).
  result: 'Pass' | 'Fail' | 'NA' | null;
  evidence_text: string | null;
  evidence_file_id: number | null;
  // Parity (migration 163): per-control Existence + Effectiveness scores (1-5).
  existence_score?: number | null;
  effectiveness_score?: number | null;
  is_active?: number;
  created_by?: number | null;
  created_on?: string | null;
  modified_by?: number | null;
  modified_on?: string | null;
}

// Parity model B — implementation row (definition from CTR) + this audit's saved result.
export interface ControlImplementationRow {
  implementation_id: number;       // ctr_implementation_steps.id
  control_id: number;
  // Plan 110-01: nullable per-risk scoping (mirrors ControlTestResponseRow.risk_id).
  risk_id?: number | null;
  // Plan 111-01: nullable per-sub-process scoping (Test of Design).
  process_step_id?: number | null;
  sequence_number?: number | null;
  implementation_type?: string | null;
  target_id?: number | null;
  explanation?: string | null;
  response_id?: number | null;     // adt_control_implementation_responses.id (null if not yet saved)
  result?: 'Pass' | 'Fail' | 'NA' | null;
  observations?: string | null;
}

// Plan 110-02 — controls-under-risk cascade for the audit RAT body.
// Backend route (plan 110-01):
//   GET /api/v1/adt/risk_controls?procedure_instance_id=N&risk_id=M
// Returns the controls mitigating a given risk (from rsk_control_maps JOIN
// ctr_register), each with its live implementation-step DEFINITIONS. Computes
// NO score — the audit body persists scores separately via the control-test /
// control-implementation response services (carrying risk_id).
export interface RiskControlRow {
  control_id: number;
  control_code?: string | null;
  control_name?: string | null;
  control_type?: string | null;
  implementations: {
    id: number;
    sequence_number?: number | null;
    implementation_type?: string | null;
    explanation?: string | null;
  }[];
}

export const riskControlsService = {
  async list(procedureInstanceId: number, riskId: number): Promise<RiskControlRow[]> {
    const resp = await apiClient.get(`${BASE}/risk_controls`, {
      params: { procedure_instance_id: procedureInstanceId, risk_id: riskId },
    });
    return Array.isArray(resp.data) ? resp.data : [];
  },
};

export const controlTestResponseService = {
  async list(procedureInstanceId: number, riskId?: number, processStepId?: number): Promise<ControlTestResponseRow[]> {
    const params: Record<string, any> = { procedure_instance_id: procedureInstanceId, limit: 500 };
    // Plan 110-02 — RAT body passes risk_id to get the per-risk saved control
    // scores (plan 110-01 backend filter). Legacy CAT body omits it.
    if (riskId != null) params.risk_id = riskId;
    // Plan 111-02 — Test of Design body passes process_step_id so the per-sub-process
    // saved control scores are returned (plan 111-01 backend filter).
    if (processStepId != null) params.process_step_id = processStepId;
    const resp = await apiClient.get(`${BASE}/control_test_responses`, { params });
    const data = resp.data?.data ?? resp.data ?? [];
    return Array.isArray(data) ? data : [];
  },
  async create(
    payload: Partial<ControlTestResponseRow> & {
      procedure_instance_id: number;
      control_id: number;
      // Optional under parity model B — a control row may be created with only
      // Existence/Effectiveness scores (results live per-implementation).
      result?: 'Pass' | 'Fail' | 'NA' | null;
      created_by: number;
      entity_id: number;
    },
  ): Promise<ControlTestResponseRow> {
    const resp = await apiClient.post(`${BASE}/control_test_responses`, payload);
    return resp.data as ControlTestResponseRow;
  },
  async update(
    id: number,
    payload: Partial<ControlTestResponseRow> & { modified_by: number },
  ): Promise<ControlTestResponseRow> {
    const resp = await apiClient.put(`${BASE}/control_test_responses/${id}`, payload);
    return resp.data as ControlTestResponseRow;
  },
};

// Parity model B — per-implementation control-test results (read defs from CTR).
export const controlImplResponseService = {
  async list(procedureInstanceId: number, controlId?: number, riskId?: number, processStepId?: number): Promise<ControlImplementationRow[]> {
    const params: Record<string, any> = { procedure_instance_id: procedureInstanceId };
    if (controlId != null) params.control_id = controlId;
    // Plan 110-02 — when scoping per-risk (RAT body) pass risk_id so the saved
    // result rows returned are the per-risk ones (plan 110-01 backend filter).
    if (riskId != null) params.risk_id = riskId;
    // Plan 111-02 — Test of Design body passes process_step_id so the saved result
    // rows are the per-sub-process ones (plan 111-01 backend JOIN filter).
    if (processStepId != null) params.process_step_id = processStepId;
    const resp = await apiClient.get(`${BASE}/control_implementation_responses`, { params });
    return Array.isArray(resp.data) ? resp.data : [];
  },
  async create(payload: {
    procedure_instance_id: number;
    control_id: number;
    implementation_id: number;
    // Plan 110-02 — per-risk scoping when created from a RiskAssessment body.
    risk_id?: number | null;
    // Plan 111-02 — per-sub-process scoping when created from a Test of Design body.
    process_step_id?: number | null;
    result?: 'Pass' | 'Fail' | 'NA' | null;
    observations?: string | null;
  }): Promise<{ id: number } & ControlImplementationRow> {
    const resp = await apiClient.post(`${BASE}/control_implementation_responses`, payload);
    return resp.data;
  },
  async update(id: number, payload: {
    // Plan 110-02 — per-risk scoping (additive; legacy callers omit it).
    risk_id?: number | null;
    // Plan 111-02 — per-sub-process scoping (additive; legacy callers omit it).
    process_step_id?: number | null;
    result?: 'Pass' | 'Fail' | 'NA' | null;
    observations?: string | null;
  }): Promise<{ id: number } & ControlImplementationRow> {
    const resp = await apiClient.put(`${BASE}/control_implementation_responses/${id}`, payload);
    return resp.data;
  },
};

// --- Samples (Plan 05b-02 / STAFF-04) ---
// Backend routes use hyphenated paths: /adt/sample-sets and /adt/sample-items.

export interface SampleSet {
  id: number;
  procedure_instance_id: number;
  population_id: number;
  sample_set_name?: string | null;
  sample_code?: string | null;
  description?: string | null;
  entity_id?: number | null;
  is_active?: number;
}

export interface SampleItem {
  id: number;
  sample_set_id: number;
  population_item_id?: number | null;
  item_label?: string | null;
  item_data?: Record<string, any> | null;
  entity_id?: number | null;
  is_active?: number;
}

export const sampleService = {
  async listSets(procedureInstanceId: number): Promise<SampleSet[]> {
    const resp = await apiClient.get(`${BASE}/sample-sets`, {
      params: { procedure_instance_id: procedureInstanceId, limit: 50 },
    });
    const data = resp.data?.data ?? resp.data ?? [];
    return Array.isArray(data) ? data : [];
  },
  async createSet(payload: Partial<SampleSet> & { entity_id: number; procedure_instance_id: number; population_id: number; created_by: number }): Promise<SampleSet> {
    const resp = await apiClient.post(`${BASE}/sample-sets`, payload);
    return resp.data as SampleSet;
  },
  async deleteSet(id: number): Promise<void> {
    // Soft-delete; used by Manage Samples destructive re-pick path (05b-04 W5).
    await apiClient.delete(`${BASE}/sample-sets/${id}`);
  },
  async listItems(sampleSetId: number): Promise<SampleItem[]> {
    const resp = await apiClient.get(`${BASE}/sample-items`, {
      params: { sample_set_id: sampleSetId, limit: 500 },
    });
    const data = resp.data?.data ?? resp.data ?? [];
    return Array.isArray(data) ? data : [];
  },
  async createItem(payload: Partial<SampleItem> & { entity_id: number; sample_set_id: number; created_by: number }): Promise<SampleItem> {
    const resp = await apiClient.post(`${BASE}/sample-items`, payload);
    return resp.data as SampleItem;
  },
  async deleteItem(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/sample-items/${id}`);
  },
  async listPopulationItems(populationId: number): Promise<any[]> {
    const resp = await apiClient.get(`${BASE}/populations/${populationId}`);
    const items = (resp.data as any)?.items ?? [];
    return Array.isArray(items) ? items : [];
  },
  // 2026-05-18 — Geographies-as-sample-pool bridge. Idempotently provisions a
  // 'Geographies' Population for the tenant and returns ORG_locations as
  // virtual sample items. SamplePickerStep uses this so auditors don't have to
  // pick a Population by hand.
  async getSamplePool(): Promise<{ population_id: number; items: Array<{ id: number; label: string; source: string }> }> {
    const resp = await apiClient.get(`${BASE}/sample-pool`);
    const data = resp.data ?? {};
    return {
      population_id: Number(data.population_id),
      items: Array.isArray(data.items) ? data.items : [],
    };
  },
  /** Materialise one child procedure_instance per sample_item for this parent.
   *  Requires migration 126. Returns {children, parent_id, count}. */
  async spawnSampleInstances(procedureInstanceId: number): Promise<{
    children: Array<{ id: number; procedure_name: string; sample_item_id: number; sample_item_name: string }>;
    parent_id: number;
    count: number;
  }> {
    const resp = await apiClient.post(
      `${BASE}/procedure_instances/${procedureInstanceId}/spawn-sample-instances`,
    );
    return resp.data;
  },

  /**
   * Append NEW samples to an already-rolled-out procedure WITHOUT wiping existing ones.
   * POST /procedure_instances/{id}/add-samples
   * REQ-2/5 (113-01): maker/approver/dates/geography forwarded to child INSERT.
   */
  async addSamples(procId: number, payload: ScopePayload): Promise<any> {
    const resp = await apiClient.post(
      `${BASE}/procedure_instances/${procId}/add-samples`,
      payload,
    );
    return resp.data;
  },

  /**
   * Bridge a saved DA Audit Selection into sample instances.
   * POST /procedure_instances/{id}/import-selection (113-01 bridge endpoint).
   */
  async importSelection(procId: number, payload: ImportSelectionPayload): Promise<{
    count: number;
    children: Array<{ id: number; procedure_name: string; sample_item_id: number; sample_item_name: string }>;
    selection_id: string;
  }> {
    const resp = await apiClient.post(
      `${BASE}/procedure_instances/${procId}/import-selection`,
      payload,
    );
    return resp.data;
  },
};

// --- Populations (Plan 05b-02 / STAFF-04 + 106-03 / R6 / D-02) ---
// Used by SamplePickerStep step 2a (performer pick) AND PopulationMaster CRUD tab.
// Endpoints: /api/v1/adt/populations  (GET paginated, POST, PUT /{id}, DELETE /{id})

export interface Population {
  id: number;
  entity_id?: number;
  code_seq?: number | null;
  code?: string | null;
  population_name: string;
  /** Legacy alias used by SamplePickerStep; prefer population_description for new code. */
  description?: string | null;
  population_description?: string | null;
  is_active?: number;
  workflow_state?: string;
  is_approved?: number;
  created_by?: number | null;
  created_on?: string;
  modified_by?: number | null;
  modified_on?: string;
}

/** Maker-checker express save target (mirrors WorkflowSaveBar SaveTarget). */
export type PopulationSaveTarget = 'draft' | 'submit' | 'approve';

export interface PopulationCreatePayload {
  population_name: string;
  population_description?: string | null;
}

export interface PopulationUpdatePayload {
  population_name?: string;
  population_description?: string | null;
  is_active?: number;
}

export const populationService = {
  /** Legacy call used by SamplePickerStep — fetches by entity_id param. */
  async list(entityId: number): Promise<Population[]> {
    const resp = await apiClient.get(`${BASE}/populations`, {
      params: { entity_id: entityId, limit: 200 },
    });
    const data = resp.data?.data ?? resp.data ?? [];
    return Array.isArray(data) ? data : [];
  },

  /**
   * Approved + active populations for dropdowns. Hits the /dropdown endpoint,
   * which filters is_active = 1 AND workflow_state = 'approved' — so draft and
   * inactive rows never leak into selectors. Shaped as {id, population_name}
   * so callers that render p.population_name stay unchanged.
   */
  async dropdown(): Promise<Population[]> {
    const resp = await apiClient.get(`${BASE}/populations/dropdown`);
    const items: Array<{ id: number; name: string }> = resp.data?.items ?? [];
    return items.map((i) => ({ id: i.id, population_name: i.name })) as Population[];
  },

  /** Fetch all active populations for the current entity (up to 500). */
  async listPopulations(): Promise<Population[]> {
    const resp = await apiClient.get(`${BASE}/populations`, {
      params: { page: 1, limit: 500, sort: 'id' },
    });
    const raw = resp.data;
    const items = Array.isArray(raw) ? raw : (raw?.data ?? []);
    return items as Population[];
  },

  /** Create a new population label (maker-checker express target). */
  async createPopulation(body: PopulationCreatePayload, action: PopulationSaveTarget = 'draft'): Promise<Population> {
    const resp = await apiClient.post(`${BASE}/populations?action=${action}`, {
      ...body,
      is_active: 1,
    });
    return resp.data as Population;
  },

  /** Update an existing population (name, description, or is_active). */
  async updatePopulation(id: number, body: PopulationUpdatePayload, action: PopulationSaveTarget = 'draft'): Promise<Population> {
    const resp = await apiClient.put(`${BASE}/populations/${id}?action=${action}`, body);
    return resp.data as Population;
  },

  /** Soft-delete a population (backend sets is_active = -1). */
  async deletePopulation(id: number): Promise<void> {
    await apiClient.delete(`${BASE}/populations/${id}`);
  },
};

// --- Findings (Plan 05b-02 / STAFF-06) ---
// Inline-raise from response rows. Existing findingsService.list (line 878) is
// the DRC tab's filtered list; this block adds create + a procedure-scoped list.

export interface FindingCreatePayload {
  entity_id: number;
  procedure_instance_id: number;
  item_type_id?: number | null;
  item_record_id?: number | null;
  findings_text: string;
  raised_on_date?: string | null;
  response_user_id?: number | null;
  is_trivial?: boolean;
  is_closed?: boolean;
  is_active?: number;
  created_by: number;
  // Bridge fields — only for CONTROL/RISK findings; feed the source='audit' control
  // deficiency / risk vulnerability the finding creates (5-level severity + title).
  bridge_severity?: 'Critical' | 'High' | 'Medium' | 'Low' | 'Informational' | null;
  bridge_title?: string | null;
}

// Phase 08 — finding allocation payload.
export interface FindingAllocatePayload {
  allocated_to_user_id: number;
  allocation_due_date?: string | null;   // ISO YYYY-MM-DD
  allocation_note?: string | null;
}

export interface BulkAllocatePayload extends FindingAllocatePayload {
  finding_ids: number[];
}

export interface BulkAllocateResultItem {
  finding_id: number;
  success: boolean;
  error?: string | null;
}

export interface BulkAllocateResult {
  allocated: number;
  failed: number;
  results: BulkAllocateResultItem[];
}

export const findingService = {
  async create(payload: FindingCreatePayload): Promise<Finding> {
    const resp = await apiClient.post(`${BASE}/findings`, payload);
    return resp.data as Finding;
  },
  /** Escalate a CONTROL/RISK finding into the register — creates a source='audit'
   *  control deficiency / risk vulnerability (idempotent). */
  async pushToDeficiency(findingId: number): Promise<{ success: boolean; already_pushed: boolean; id: number; kind: 'deficiency' | 'vulnerability' }> {
    const resp = await apiClient.post(`${BASE}/findings/${findingId}/push-to-deficiency`);
    return resp.data;
  },
  /** Edit descriptive finding fields (findings_text only today). Lifecycle-safe —
   *  backend PUT whitelist forbids state columns. */
  async update(findingId: number, payload: { findings_text?: string }): Promise<Finding> {
    const resp = await apiClient.put(`${BASE}/findings/${findingId}`, payload);
    return resp.data as Finding;
  },
  /** Phase 08 — GRC Admin allocates a released finding to an org member. */
  async allocate(findingId: number, payload: FindingAllocatePayload): Promise<Finding> {
    const resp = await apiClient.post(`${BASE}/findings/${findingId}/allocate`, payload);
    return resp.data as Finding;
  },
  /** Phase 08 — bulk allocate N findings to the same user. */
  async bulkAllocate(payload: BulkAllocatePayload): Promise<BulkAllocateResult> {
    const resp = await apiClient.post(`${BASE}/findings/bulk-allocate`, payload);
    return resp.data as BulkAllocateResult;
  },
  /** Phase 08 — push finding into the report (eligible for publish). */
  async pushToReport(findingId: number): Promise<Finding> {
    const resp = await apiClient.post(`${BASE}/findings/${findingId}/push-to-report`, {});
    return resp.data as Finding;
  },
  /** Phase 08 — undo push-to-report (only before report is released). */
  async withdrawFromReport(findingId: number): Promise<Finding> {
    const resp = await apiClient.post(`${BASE}/findings/${findingId}/withdraw-from-report`, {});
    return resp.data as Finding;
  },
  /** Which reports + sections this finding currently sits in. */
  async reportMemberships(findingId: number): Promise<FindingReportMembership[]> {
    const resp = await apiClient.get(`${BASE}/findings/${findingId}/report-memberships`);
    return Array.isArray(resp.data) ? (resp.data as FindingReportMembership[]) : [];
  },
  /** Phase 08 — staff portal bucket: findings allocated to the current user. */
  async allocatedToMe(includeClosed = false): Promise<{ data: Finding[]; meta: { page: number; limit: number; total: number } }> {
    const resp = await apiClient.get(`${BASE}/findings/allocated-to-me`, {
      params: { include_closed: includeClosed },
    });
    return resp.data;
  },
  async listForProcedure(procedureInstanceId: number): Promise<Finding[]> {
    // Prefer server-side filter; fall back to client-side filter only if the
    // backend rejects the param (e.g. older endpoint version).
    try {
      const resp = await apiClient.get(`${BASE}/findings`, {
        params: { procedure_instance_id: procedureInstanceId, limit: 500 },
      });
      const data: Finding[] = resp.data?.data ?? resp.data ?? [];
      return Array.isArray(data) ? data.filter((f) => f.procedure_instance_id === procedureInstanceId) : [];
    } catch {
      const resp = await apiClient.get(`${BASE}/findings`, { params: { limit: 500 } });
      const data: Finding[] = resp.data?.data ?? resp.data ?? [];
      return Array.isArray(data) ? data.filter((f) => f.procedure_instance_id === procedureInstanceId) : [];
    }
  },
};

// --- File uploads (Plan 05b-02 / supports STAFF-06 evidence upload in 05b-05) ---
// Wraps POST /api/v1/utl/files (multipart/form-data). ObservationModal in 05b-05
// MUST use this service — no inline apiClient calls for evidence upload.

export interface UploadedFile {
  id: number;
  file_name: string;
  [key: string]: any;
}

export const fileService = {
  async upload(file: File, entityId: number, createdBy: number): Promise<UploadedFile> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_id', String(entityId));
    formData.append('created_by', String(createdBy));
    const resp = await apiClient.post('/api/v1/utl/files', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return resp.data as UploadedFile;
  },
  /** Lightweight metadata — real filename / mime, for showing alongside the badge. */
  async meta(fileId: number): Promise<{ id: number; file_name: string; file_size_kb: number; mime_type: string }> {
    const resp = await apiClient.get(`/api/v1/utl/files/${fileId}`);
    return resp.data;
  },
  /** Fetch the bytes through the authed client (so the Bearer token is sent) and
   *  open them in a new tab. Images/PDFs render inline; other types download. */
  async open(fileId: number): Promise<void> {
    const res = await apiClient.get(`/api/v1/utl/files/${fileId}/download`, { responseType: 'blob' });
    const type = (res.headers?.['content-type'] as string) || 'application/octet-stream';
    const url = URL.createObjectURL(new Blob([res.data], { type }));
    window.open(url, '_blank', 'noopener');
    // Revoke after a minute — long enough for the new tab to load the blob.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
  /** Force a browser DOWNLOAD (save dialog) rather than an inline preview — the
   *  same UX as the Findings evidence tab. Streams the blob via the authed client
   *  and triggers an anchor click with the real filename. */
  async download(fileId: number, fallbackName = 'evidence'): Promise<void> {
    const res = await apiClient.get(`/api/v1/utl/files/${fileId}/download`, { responseType: 'blob' });
    const type = (res.headers?.['content-type'] as string) || 'application/octet-stream';
    const disp = (res.headers?.['content-disposition'] as string | undefined) || '';
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(disp);
    const filename = match ? decodeURIComponent(match[1] || match[2]) : fallbackName;
    const url = URL.createObjectURL(new Blob([res.data], { type }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

/** A file attached to one procedure question (junction row + JOINed UTL_files cols). */
export interface ProcedureEvidence {
  id: number;
  procedure_instance_id: number;
  item_type_id: number | null;
  item_record_id: number | null;
  file_id: number;
  attached_by: number | null;
  attached_on: string;
  is_active: number;
  file_name?: string | null;
  mime_type?: string | null;
  file_size_kb?: number | null;
}

/** Per-question multi-evidence — the shared surface behind every procedure body's
 *  paperclip. list/upload/remove mirror findingEvidenceService; viewing a row
 *  reuses fileService.open(file_id). */
export const procedureEvidenceService = {
  async list(
    procedureInstanceId: number,
    itemTypeId?: number | null,
    itemRecordId?: number | null,
  ): Promise<ProcedureEvidence[]> {
    const params: Record<string, number> = { procedure_instance_id: procedureInstanceId };
    if (itemTypeId != null) params.item_type_id = itemTypeId;
    if (itemRecordId != null) params.item_record_id = itemRecordId;
    const resp = await apiClient.get(`${BASE}/procedure-evidence`, { params });
    const data = resp.data ?? [];
    return Array.isArray(data) ? (data as ProcedureEvidence[]) : [];
  },
  /** Two-step: upload bytes to UTL_files, then link the returned file_id. */
  async upload(
    file: File,
    procedureInstanceId: number,
    itemTypeId: number | null,
    itemRecordId: number | null,
    entityId: number,
    currentUserId: number,
  ): Promise<ProcedureEvidence> {
    const uploaded = await fileService.upload(file, entityId, currentUserId);
    const resp = await apiClient.post(`${BASE}/procedure-evidence`, {
      procedure_instance_id: procedureInstanceId,
      item_type_id: itemTypeId,
      item_record_id: itemRecordId,
      file_id: uploaded.id,
    });
    return resp.data as ProcedureEvidence;
  },
  async remove(evidenceId: number): Promise<void> {
    await apiClient.delete(`${BASE}/procedure-evidence/${evidenceId}`);
  },
};

// ============================================================================
// ============================================================================
// Phase 106 / plan 06 — Program Lead + Staff service functions (R4, R5)
// Endpoints: listProgramsForLead, listProceduresForProgram, scopeProcedure,
// allocateTask, getMyWork.
// ============================================================================

/** Extended ProcedureInstance with scope/allocate fields added by 106-04. */
export interface ProcedureInstanceFull extends ProcedureInstance {
  /** Maker user_id (set by allocate-task) */
  maker_user_id?: number | null;
  /** Approver user_id (set by allocate-task) */
  approver_user_id_task?: number | null;
  /** Population id scoped to (set by /scope) */
  population_id?: number | null;
  /** Sample CSV sent to /scope */
  sample_csv?: string | null;
  // Phase-106 PL tree/rollout — denormalized by the list endpoint:
  /** This procedure's sample label (set once rolled out into a sample child). */
  sample_item_id?: number | null;
  sample_item_name?: string | null;
  /** Display name of the assigned maker / checker. */
  maker_user_name?: string | null;
  approver_user_name?: string | null;
  /** Parent procedure (set on spawned sample children); name for the tree header. */
  parent_id?: number | null;
  parent_name?: string | null;
  /** Population the sample is drawn from (denormalized for the PL tree). */
  population_name?: string | null;
  /** Rollout start date (ISO). due_date already exists on the base type. */
  start_date?: string | null;
  /** Template procedure type (denormalized on the single-instance GET; may be
   *  absent on the paginated list response). Drives the type pill in the PL tree. */
  procedure_type?: string | null;
}

export interface ScopePayload {
  population_id?: number | null;
  sample_csv?: string | null;
  /** Optional rollout dates (ISO 'YYYY-MM-DD') applied to spawned tasks. */
  start_date?: string | null;
  due_date?: string | null;
  /** REQ-2 — maker/checker forwarded to every spawned child procedure_instance. */
  maker_user_id?: number | null;
  approver_user_id?: number | null;
  /** REQ-5 — geography tag persisted onto every spawned child ADT_sample_sets row. */
  geography_mode?: string | null;
  geography_location_ids?: number[] | null;
  geography_root_location_id?: number | null;
  geography_type_id?: number | null;
}

/** Request body for POST /procedure_instances/{id}/import-selection (113-01 bridge). */
export interface ImportSelectionPayload {
  /** dac_audit_selections.transaction_id (string UUID hex). */
  selection_id: string;
  /** Subset of dac_audit_selections.id rows; omit = entire selection. */
  row_ids?: number[] | null;
  population_id?: number | null;
  maker_user_id?: number | null;
  approver_user_id?: number | null;
  start_date?: string | null;
  due_date?: string | null;
}

export interface AllocateTaskPayload {
  maker_user_id?: number | null;
  approver_user_id?: number | null;
}

export const plStaffService = {
  /**
   * R4 — Programs where the current user is the Program Lead.
   * GET /program_instances?program_lead_user_id=me (entity-scoped, backend filters by JWT).
   */
  async listProgramsForLead(): Promise<ProgramInstance[]> {
    const resp = await apiClient.get(`${BASE}/program_instances`, {
      params: { program_lead_user_id: 'me', limit: 200 },
    });
    const data = resp.data;
    if (Array.isArray(data)) return data as ProgramInstance[];
    if (data && Array.isArray(data.data)) return data.data as ProgramInstance[];
    return [];
  },

  /**
   * R4, R5 — Procedure instances for a given program.
   * GET /procedure_instances?program_id=X (returns rows with due_date for calendar).
   */
  async listProceduresForProgram(programId: number): Promise<ProcedureInstanceFull[]> {
    const resp = await apiClient.get(`${BASE}/procedure_instances`, {
      params: { program_instance_id: programId, limit: 500 },
    });
    const data = resp.data;
    if (data && Array.isArray(data.data)) return data.data as ProcedureInstanceFull[];
    if (Array.isArray(data)) return data as ProcedureInstanceFull[];
    return [];
  },

  /**
   * R4 — Scope a procedure: set population + optional sample CSV.
   * POST /procedure_instances/{id}/scope
   */
  async scopeProcedure(procId: number, payload: ScopePayload): Promise<any> {
    const resp = await apiClient.post(
      `${BASE}/procedure_instances/${procId}/scope`,
      payload,
    );
    return resp.data;
  },

  /**
   * Append NEW samples to an already-rolled-out procedure WITHOUT wiping existing ones.
   * POST /procedure_instances/{id}/add-samples → { count, children:[{id, sample_item_name}] }
   * REQ-2: body.maker_user_id/approver_user_id/start_date/due_date go into child INSERT.
   * REQ-5: body geography fields persisted onto child ADT_sample_sets row (113-01 rewire).
   */
  async addSamples(procId: number, payload: ScopePayload): Promise<any> {
    const resp = await apiClient.post(
      `${BASE}/procedure_instances/${procId}/add-samples`,
      payload,
    );
    return resp.data;
  },

  /**
   * Bridge a saved DA Audit Selection into sample instances.
   * POST /procedure_instances/{id}/import-selection (113-01 bridge endpoint).
   * Spawns one child procedure_instance + ADT_sample_sets row per DA row.
   * REQ-2: maker/approver/start/due forwarded to each child INSERT.
   * REQ-4: registered-path rollout.
   * Returns { count, children, selection_id }.
   */
  async importSelection(procId: number, payload: ImportSelectionPayload): Promise<{
    count: number;
    children: Array<{ id: number; procedure_name: string; sample_item_id: number; sample_item_name: string }>;
    selection_id: string;
  }> {
    const resp = await apiClient.post(
      `${BASE}/procedure_instances/${procId}/import-selection`,
      payload,
    );
    return resp.data;
  },

  /**
   * R4 — Allocate maker/approver to a procedure task.
   * PUT /procedure_instances/{id}/allocate-task
   */
  async allocateTask(procId: number, payload: AllocateTaskPayload): Promise<any> {
    const resp = await apiClient.put(
      `${BASE}/procedure_instances/${procId}/allocate-task`,
      payload,
    );
    return resp.data;
  },

  /**
   * R5 / D-08 — My work buckets for staff.
   * Wraps the existing myWorkService but exposed here for co-location.
   * include: CSV buckets e.g. ['due','active','my_approvals']
   */
  async getMyWork(include?: MyWorkBucketName[], windowDays?: number): Promise<MyWorkResponse> {
    return myWorkService.get({ include, window_days: windowDays });
  },
};

// --- Finding evidence (Plan 06-03 / FIND-03) ---
// N files per finding. Two-call attach pattern: (1) fileService.upload (existing,
// above) uploads bytes to UTL_files and returns {id, file_name}; (2) this service
// POSTs {file_id} to /adt/findings/{id}/evidence to create the junction row.
// Mirror pattern: ObservationModal in 05b-05 composes the same two calls for
// inline finding evidence at raise time.
// ============================================================================

export interface FindingEvidence {
  id: number;
  finding_id: number;
  file_id: number;
  attached_by: number;
  attached_on: string;
  is_active: number;
  /** JOINed UTL_files cols — populated by the backend SELECT-back. */
  file_name?: string | null;
  mime_type?: string | null;
  file_size_kb?: number | null;
}

// --- Audit Memos (Phase 106 R12) ---
// Findings surfaced as "Audit Memos" at the presentation layer.
// GET  /adt/memos             — paginated list with lifecycle state + report_eligible
// PATCH /adt/memos/{id}/report-eligibility — toggle report_eligible flag
//
// A memo IS a finding — the Finding interface is reused; extra fields are
// populated by the /memos endpoint JOIN query (procedure_name, program_name,
// lifecycle_state, report_eligible).

export interface MemoListParams {
  page: number;
  limit: number;
  /** Filter by derived lifecycle state: 'Raised' | 'Responded' | 'Closed' */
  state?: string;
  /** Filter by report_eligible flag */
  report_eligible?: boolean;
}

/** A memo item is a Finding enriched with lifecycle_state + report_eligible. */
export interface MemoItem extends Finding {
  /** Derived: 'Raised' | 'Responded' | 'Closed' */
  lifecycle_state?: string | null;
  /** Migration 143 — eligible to be selected into an audit report */
  report_eligible?: boolean | null;
}

export const memosService = {
  async list(opts: MemoListParams): Promise<PagedResult<MemoItem>> {
    const params: Record<string, any> = {
      page: opts.page,
      limit: opts.limit,
    };
    if (opts.state !== undefined) params.state = opts.state;
    if (opts.report_eligible !== undefined) params.report_eligible = opts.report_eligible;
    const resp = await apiClient.get(`${BASE}/memos`, { params });
    const data = resp.data ?? {};
    return {
      items: Array.isArray(data.data) ? (data.data as MemoItem[]) : [],
      total: data.meta?.total ?? 0,
    };
  },

  async setReportEligibility(id: number, eligible: boolean): Promise<MemoItem> {
    const resp = await apiClient.patch(`${BASE}/memos/${id}/report-eligibility`, {
      report_eligible: eligible,
    });
    return resp.data as MemoItem;
  },
};

// --- Communications Channel (Phase 106 R13 — Queries Raised) ---
// GET  /adt/communications               — list threads (channel + status filters)
// POST /adt/communications               — create thread + first message (1 txn)
// GET  /adt/communications/{id}          — thread detail with messages
// POST /adt/communications/{id}/messages — post a reply
// PATCH /adt/communications/{id}         — close / reopen
// ============================================================================

export interface CommThread {
  id: number;
  entity_id?: number | null;
  subject: string;
  /** 'internal' | 'external' */
  channel: string;
  /** 'open' | 'closed' */
  status: string;
  raised_by: number;
  raised_by_name?: string | null;
  procedure_instance_id?: number | null;
  program_instance_id?: number | null;
  procedure_instance_name?: string | null;
  program_instance_code?: string | null;
  program_instance_description?: string | null;
  finding_id?: number | null;
  agency_id?: number | null;
  message_count?: number;
  last_message_at?: string | null;
  is_active?: number;
  created_by?: number | null;
  created_on?: string | null;
  modified_by?: number | null;
  modified_on?: string | null;
}

export interface CommMessage {
  id: number;
  entity_id?: number | null;
  thread_id: number;
  body: string;
  sender_user_id: number;
  sender_name?: string | null;
  attachment_file_id?: number | null;
  attachment_file_name?: string | null;
  is_active?: number;
  created_by?: number | null;
  created_on?: string | null;
}

export interface CommThreadDetail extends CommThread {
  messages: CommMessage[];
}

export interface CommThreadListParams {
  page: number;
  limit: number;
  channel?: 'internal' | 'external';
  status?: 'open' | 'closed';
  recipient_only?: boolean;
  search?: string;
}

export interface CommThreadCreateParams {
  subject: string;
  channel: 'internal' | 'external';
  first_message: string;
  procedure_instance_id?: number;
  program_instance_id?: number;
  finding_id?: number;
  attachment_file_id?: number;
  recipient_user_ids?: number[];
}

export const communicationsService = {
  async list(opts: CommThreadListParams): Promise<PagedResult<CommThread>> {
    const params: Record<string, any> = { page: opts.page, limit: opts.limit };
    if (opts.channel) params.channel = opts.channel;
    if (opts.status) params.status = opts.status;
    if (opts.recipient_only) params.recipient_only = true;
    if (opts.search?.trim()) params.search = opts.search.trim();
    const resp = await apiClient.get(`${BASE}/communications`, { params });
    const data = resp.data ?? {};
    return {
      items: Array.isArray(data.data) ? (data.data as CommThread[]) : [],
      total: data.meta?.total ?? 0,
    };
  },

  async create(body: CommThreadCreateParams): Promise<CommThreadDetail> {
    const resp = await apiClient.post(`${BASE}/communications`, body);
    return resp.data as CommThreadDetail;
  },

  async get(id: number): Promise<CommThreadDetail> {
    const resp = await apiClient.get(`${BASE}/communications/${id}`);
    return resp.data as CommThreadDetail;
  },

  async postMessage(id: number, body: string, attachmentFileId?: number | null): Promise<CommMessage> {
    const resp = await apiClient.post(`${BASE}/communications/${id}/messages`, {
      body,
      ...(attachmentFileId ? { attachment_file_id: attachmentFileId } : {}),
    });
    return resp.data as CommMessage;
  },

  async setStatus(id: number, status: 'open' | 'closed'): Promise<CommThread> {
    const resp = await apiClient.patch(`${BASE}/communications/${id}`, { status });
    return resp.data as CommThread;
  },

  /** Detach the file from a message. Returns the updated message (attachment nulled). */
  async deleteMessageAttachment(threadId: number, messageId: number): Promise<CommMessage> {
    const resp = await apiClient.delete(`${BASE}/communications/${threadId}/messages/${messageId}/attachment`);
    return resp.data as CommMessage;
  },
};

export const findingEvidenceService = {
  /** Two-step: upload bytes to UTL_files, then attach the returned file_id. */
  async upload(
    file: File,
    findingId: number,
    entityId: number,
    currentUserId: number,
  ): Promise<FindingEvidence> {
    const uploaded = await fileService.upload(file, entityId, currentUserId);
    const resp = await apiClient.post(
      `${BASE}/findings/${findingId}/evidence`,
      { file_id: uploaded.id },
    );
    return resp.data as FindingEvidence;
  },

  async list(findingId: number): Promise<FindingEvidence[]> {
    const resp = await apiClient.get(`${BASE}/findings/${findingId}/evidence`);
    const data = resp.data ?? [];
    return Array.isArray(data) ? (data as FindingEvidence[]) : [];
  },

  async remove(findingId: number, evidenceId: number): Promise<void> {
    await apiClient.delete(`${BASE}/findings/${findingId}/evidence/${evidenceId}`);
  },

  /**
   * G5 (phase 108) — Download an evidence file. Streams the backend
   * FileResponse (or follows the S3 presigned redirect axios resolves
   * transparently) into a Blob, then triggers a browser download via an
   * anchor click. On failure (404/503 when storage is unavailable in dev)
   * the JSON error detail is decoded from the blob and re-thrown so the
   * caller can surface a graceful message instead of crashing.
   */
  async download(
    findingId: number,
    evidenceId: number,
    fallbackName = 'evidence',
  ): Promise<void> {
    try {
      const res = await apiClient.get(
        `${BASE}/findings/${findingId}/evidence/${evidenceId}/download`,
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], {
        type: (res.headers?.['content-type'] as string | undefined) || 'application/octet-stream',
      });
      const disp = (res.headers?.['content-disposition'] as string | undefined) || '';
      const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(disp);
      const filename = match ? decodeURIComponent(match[1] || match[2]) : fallbackName;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      // Error bodies arrive as a Blob when responseType is 'blob'; decode the
      // FastAPI {detail} so the UI can show a human-readable message.
      let detail = 'Download failed.';
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          detail = parsed?.detail ?? detail;
        } catch {
          /* non-JSON blob — keep the generic message */
        }
      } else if (typeof data?.detail === 'string') {
        detail = data.detail;
      }
      throw new Error(detail);
    }
  },
};

// --- Report Template Master (Phase 106 R14) ---
// GRC-side: GET/POST/GET/{id}/PUT/{id} /adt/report-templates
// Audit-side: GET/POST /adt/reports, GET/{id}, PUT/{id}/memos, POST/{id}/generate
// =============================================================================

export interface ReportTemplateSection {
  key: string;
  title: string;
  order: number;
}

export interface ReportTemplate {
  id: number;
  entity_id?: number | null;
  name: string;
  description?: string | null;
  sections: ReportTemplateSection[];
  is_active?: number;
  created_by?: number | null;
  created_on?: string | null;
  modified_by?: number | null;
  modified_on?: string | null;
}

export interface ReportTemplateCreateParams {
  name: string;
  description?: string;
  sections: ReportTemplateSection[];
}

export interface ReportTemplateUpdateParams {
  name?: string;
  description?: string;
  sections?: ReportTemplateSection[];
}

export interface ReportMemoItem {
  id: number;
  entity_id?: number | null;
  report_id: number;
  finding_id: number;
  section_key: string;
  sort_order: number;
  findings_text?: string | null;
  report_eligible?: boolean | null;
  lifecycle_state?: string | null;
  procedure_name?: string | null;
  program_code?: string | null;
  is_active?: number;
  created_by?: number | null;
  created_on?: string | null;
}

export interface Report {
  id: number;
  entity_id?: number | null;
  report_template_id: number;
  title: string;
  program_instance_id?: number | null;
  /** 'draft' | 'generated' */
  status: string;
  generated_at?: string | null;
  is_active?: number;
  created_by?: number | null;
  created_on?: string | null;
  modified_by?: number | null;
  modified_on?: string | null;
  template_name?: string | null;
  template_sections?: ReportTemplateSection[] | null;
  /** Only populated in GET /reports/{id} */
  memos?: ReportMemoItem[] | null;
  /** The drafted narrative per paragraph — THIS is the report. The memos above
   *  are findings linked to a paragraph as an enabler/annexure. */
  paragraphs?: ReportParagraph[] | null;
}

/** One drafted paragraph of the audit report (ADT_report_paragraphs). */
export interface ReportParagraph {
  id?: number;
  section_key: string;
  body_text?: string | null;
  sort_order?: number;
  created_by?: number | null;
  created_on?: string | null;
  modified_by?: number | null;
  modified_on?: string | null;
}

export interface ReportCreateParams {
  report_template_id: number;
  title: string;
  program_instance_id?: number;
}

export interface ReportMemoAssignment {
  finding_id: number;
  section_key: string;
  sort_order: number;
}

export const reportTemplatesService = {
  async list(opts: { page: number; limit: number }): Promise<PagedResult<ReportTemplate>> {
    const resp = await apiClient.get(`${BASE}/report-templates`, {
      params: { page: opts.page, limit: opts.limit },
    });
    const data = resp.data ?? {};
    return {
      items: Array.isArray(data.data) ? (data.data as ReportTemplate[]) : [],
      total: data.meta?.total ?? 0,
    };
  },

  async create(body: ReportTemplateCreateParams): Promise<ReportTemplate> {
    const resp = await apiClient.post(`${BASE}/report-templates`, body);
    return resp.data as ReportTemplate;
  },

  async get(id: number): Promise<ReportTemplate> {
    const resp = await apiClient.get(`${BASE}/report-templates/${id}`);
    return resp.data as ReportTemplate;
  },

  async update(id: number, body: ReportTemplateUpdateParams): Promise<ReportTemplate> {
    const resp = await apiClient.put(`${BASE}/report-templates/${id}`, body);
    return resp.data as ReportTemplate;
  },
};

export const reportsService = {
  async list(opts: { page: number; limit: number }): Promise<PagedResult<Report>> {
    const resp = await apiClient.get(`${BASE}/reports`, {
      params: { page: opts.page, limit: opts.limit },
    });
    const data = resp.data ?? {};
    return {
      items: Array.isArray(data.data) ? (data.data as Report[]) : [],
      total: data.meta?.total ?? 0,
    };
  },

  async create(body: ReportCreateParams): Promise<Report> {
    const resp = await apiClient.post(`${BASE}/reports`, body);
    return resp.data as Report;
  },

  async get(id: number): Promise<Report> {
    const resp = await apiClient.get(`${BASE}/reports/${id}`);
    return resp.data as Report;
  },

  async setMemos(id: number, memos: ReportMemoAssignment[]): Promise<Report> {
    const resp = await apiClient.put(`${BASE}/reports/${id}/memos`, { memos });
    return resp.data as Report;
  },

  /** Additively place ONE finding into a section (non-destructive). Used by the
   *  finding-level "Push to Report" picker. sort_order omitted → append. */
  async addMemo(
    id: number,
    body: { finding_id: number; section_key: string; sort_order?: number },
  ): Promise<Report> {
    const resp = await apiClient.post(`${BASE}/reports/${id}/memos`, body);
    return resp.data as Report;
  },

  /** Remove ONE finding from ONE report (soft-delete the memo row). */
  async removeMemo(id: number, findingId: number): Promise<void> {
    await apiClient.delete(`${BASE}/reports/${id}/memos/${findingId}`);
  },

  /** Unlink a finding from ONE paragraph, leaving its other placements intact.
   *  A finding may sit under several paragraphs, so removeMemo (which clears
   *  every placement in the report) is too blunt for a per-paragraph action. */
  async removeMemoFromSection(id: number, findingId: number, sectionKey: string): Promise<void> {
    await apiClient.delete(
      `${BASE}/reports/${id}/memos/${findingId}/section/${encodeURIComponent(sectionKey)}`,
    );
  },

  /** Link ONE finding to a SET of paragraphs — the finding-level linking the
   *  auditor works from. Sections not listed are unlinked, so the call states
   *  the finding's complete placement in this report. */
  async linkFinding(id: number, findingId: number, sectionKeys: string[]): Promise<Report> {
    const resp = await apiClient.post(`${BASE}/reports/${id}/memos/link`, {
      finding_id: findingId,
      section_keys: sectionKeys,
    });
    return resp.data as Report;
  },

  /** Upsert drafted paragraph text. Per-section upsert, NOT a full replacement,
   *  so saving one paragraph never discards another. */
  async setParagraphs(id: number, paragraphs: ReportParagraph[]): Promise<Report> {
    const resp = await apiClient.put(`${BASE}/reports/${id}/paragraphs`, { paragraphs });
    return resp.data as Report;
  },

  /** Download the whole report as one PDF — paragraphs in order, each with its
   *  linked findings as an annexure. */
  async downloadPdf(id: number, filename?: string): Promise<void> {
    const resp = await apiClient.get(`${BASE}/reports/${id}/pdf`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(filename || `audit-report-${id}`).replace(/[^\w \-.]/g, '_')}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  async generate(id: number): Promise<Report> {
    const resp = await apiClient.post(`${BASE}/reports/${id}/generate`);
    return resp.data as Report;
  },

  /** Return a report marked final to draft so it can be edited again. */
  async reopen(id: number): Promise<Report> {
    const resp = await apiClient.post(`${BASE}/reports/${id}/reopen`);
    return resp.data as Report;
  },
};

/** One placement of a finding inside a report (GET /findings/{id}/report-memberships). */
export interface FindingReportMembership {
  report_id: number;
  report_title: string;
  report_status: string;
  section_key: string;
  section_title: string;
  sort_order: number;
}

